/** The town screen's chrome: the two wooden bars and the material they are made of.
 *
 * A classic town bar is packed edge to edge. What this replaces was a title at
 * the far left, a resource strip at the far right and roughly a thousand pixels
 * of bare woodgrain in between, top and bottom - about a tenth of the screen
 * carrying nothing, wrapped around finished art.
 *
 * It also replaces the flat CSS rectangles. A button here is a plate of
 * hammered bronze in a gilt bezel with four rivets and an engraved glyph, and a
 * creature socket is a recessed frame, because those three controls are the
 * only interactive things on the screen and they set the tone for all of it.
 *
 * Nothing here decides anything (I10): every number is read from state or from
 * `townModel`, and every refusal is the kernel's own `Reason`.
 */

import type { PointerEvent as ReactPointerEvent, ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import type {
  ArmySlot, ArmyState, BuildingDef, ContentRegistry, CreatureDef, CreatureDefId, HeroDef,
  HeroState, PrimarySkills, Reason, Resources, TownState,
} from "@game/core";
import { useAtlas } from "../hooks/useAtlas.js";
import { usePhoneLayout } from "../theme/phone.js";
import { RESOURCE_ICON } from "../theme/art.js";
import { reliefDecls } from "../theme/relief.js";
import {
  BitmapText, measureBitmapText, measureDrawnBitmapText, resolveBitmapSize, romanFaceForStep,
  useBitmapScale, useDevicePixelRatio,
} from "../theme/bitmapText.js";
import { buildingIcon, creatureIcon, heroPortrait } from "./art.js";
import { compactCount, groupDigits, titleCase } from "./format.js";
import { MaybeIcon } from "./primitives.js";
import { availableOf, guildLevelOf, recruitableCreatures } from "./townModel.js";
import { chainDepth } from "./townSlots.js";
import {
  markTownPointerDrop, pastSocketSlop, socketFromPoint, townClickSwallowed, townSocketKey,
} from "./townArmyPointer.js";
import { StyleLayer, useStyleSheet } from "../theme/styleSheet.js";

// ------------------------------------------------------- the compact window

/**
 * True on the narrow layout, i.e. exactly when the screens' own stylesheet has
 * switched to its `max-width: 1600px` block.
 *
 * The socket's art box is a JavaScript number - `fitSpriteBox` picks a whole
 * divisor against it - so the box cannot step with the window from CSS, and a
 * box that stays 56x50 while the socket around it shrinks to 46 is how a
 * creature ends up wider than the wall it stands in. One query, one constant,
 * read by both halves.
 */
const COMPACT_QUERY = "(max-width: 1600px)";

export function useCompactTown(): boolean {
  const [compact, setCompact] = useState<boolean>(
    () => typeof window !== "undefined" && window.matchMedia(COMPACT_QUERY).matches,
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia(COMPACT_QUERY);
    const onChange = (): void => { setCompact(mq.matches); };
    onChange();
    mq.addEventListener("change", onChange);
    return () => { mq.removeEventListener("change", onChange); };
  }, []);
  return compact;
}

// ------------------------------------------------------- the town's own CSS

/**
 * The town screen's own rules, layered over the shared screen stylesheet.
 *
 * They live here rather than in `styles.ts` because they are about this screen
 * and nothing else, and because three of them are paired with a number in this
 * file - the socket's art box, the ledge the count stands on, and how many
 * lines of stamped name a plate holds. A rule and the constant it must agree
 * with should not be two files apart.
 *
 * Every selector is prefixed with an ancestor the shared sheet does not use, so
 * the cascade is decided by specificity rather than by which `<style>` element
 * React happened to append first.
 */
const TOWN_STYLE_ID = "hs-town-css";

/**
 * Exported so `town-place.spec.ts` can hold the grade table.
 *
 * The two towns that must NOT carry a grade are the two a critic judged
 * already at parity with their references, and "someone graded Castle" is a
 * regression no screenshot diff would flag as wrong - it would simply look
 * like a slightly different Castle. A string test is the only place that fact
 * can be written down where it fails when it stops being true.
 */
export const TOWN_CSS = `
/* --- the console: one velvet plate in the owner's colour ----------------
   the classic's town bar is the owner's colour between gilt mouldings - blue
   for the blue player, red for red, teal for Saltreach's - and it is packed edge to
   edge: the town's name, hall, fort and income in a block on the left with the
   dwelling growth tiles under it, two rows of seven busts in the middle with a
   braid between them and a status line under them, an icon column on the
   right, and the resources in a strip along the foot. Ours was the same brown
   leather as every other screen with 41% of its area bare, which is what made
   every blind round trivial. The colour is a set of custom properties the
   screen sets from the owner (see \`velvetVars\`); a neutral town keeps the
   leather. */
.hs-screen .hs-town__rows {
  height: auto;
  flex-direction: column;
  align-items: stretch;
  gap: 0;
  padding: 7px 10px 6px;
}
.hs-town__rows .hs-tfoot {
  --hs-socket-w: 132px;
  --hs-socket-h: 84px;
  --hs-tile-w: 108px;
  --hs-tile-h: 54px;
  --hs-face: 110px;
  --hs-ibtn: 50px;
  position: relative;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 0 14px;
  align-items: stretch;
  width: 100%;
  padding: 7px 8px 6px;
  color: #f2e2b8;
  background-color: var(--hs-velvet, #3d2716);
  background-image:
    radial-gradient(90% 120% at 50% 0%, var(--hs-velvet-hi, #54371f) 0%, rgba(0,0,0,0) 62%),
    repeating-linear-gradient(93deg, rgba(255,255,255,0.035) 0 1px, rgba(0,0,0,0) 1px 3px),
    repeating-linear-gradient(0deg, rgba(0,0,0,0.10) 0 1px, rgba(0,0,0,0) 1px 4px),
    linear-gradient(180deg, var(--hs-velvet-hi, #54371f) 0%, var(--hs-velvet, #3d2716) 40%, var(--hs-velvet-lo, #2c1c0f) 100%);
  /* The gilt moulding round the plate: keyline, cream lip, gold face, shade. */
  box-shadow:
    inset 0 0 0 1px #0a0704,
    inset 0 0 0 2px #e9cf92,
    inset 0 0 0 4px #b8903a,
    inset 0 0 0 5px #5d4212,
    inset 0 0 0 6px #0a0704,
    inset 0 0 40px rgba(0,0,0,0.35);
}
/* --- the left block: crest, name, hall, fort, income, and the growth tiles */
.hs-tfoot__left { display: flex; flex-direction: column; gap: 6px; padding: 6px 0 4px 6px; min-width: 0; }
.hs-tfoot__head { display: flex; align-items: stretch; gap: 8px; }
.hs-tfoot__crest {
  position: relative; flex: 0 0 auto;
  width: 74px; height: 74px; overflow: hidden; isolation: isolate;
  display: flex; align-items: flex-end; justify-content: center;
  background-color: #1d140b;
  background-image: radial-gradient(70% 60% at 50% 100%, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0) 70%),
    linear-gradient(180deg, #3f2b17 0%, #2a1b0d 60%, #17100a 100%);
  box-shadow: inset 0 0 0 1px #0a0704, inset 0 0 0 2px #b8903a, inset 0 0 0 3px #0a0704, inset 0 6px 12px rgba(0,0,0,0.55);
}
.hs-tfoot__crest .hm-icon { image-rendering: pixelated; margin-bottom: 2px; }
.hs-tfoot__title { display: flex; flex-direction: column; justify-content: space-between; gap: 4px; min-width: 0; }
.hs-tfoot__name { display: flex; align-items: center; height: 30px; color: #f8e6b4; line-height: 0; white-space: nowrap; }
.hs-tfoot__name svg { display: block; }
.hs-tfoot__marks { display: flex; align-items: center; gap: 6px; }
.hs-tfoot__mark {
  position: relative; display: flex; align-items: center; justify-content: center;
  width: 46px; height: 46px; overflow: hidden; isolation: isolate;
  background-color: #1d140b;
  background-image: linear-gradient(180deg, #3a2815 0%, #23160b 100%);
  box-shadow: inset 0 0 0 1px #0a0704, inset 0 0 0 2px #a8801f, inset 0 0 0 3px #0a0704, inset 0 4px 8px rgba(0,0,0,0.5);
}
.hs-tfoot__mark .hm-icon { image-rendering: pixelated; }
.hs-tfoot__income {
  display: flex; align-items: center; gap: 6px; height: 46px; padding: 0 8px 0 4px;
  color: #ffe9b0; line-height: 0;
  background-color: #1d140b;
  background-image: linear-gradient(180deg, #3a2815 0%, #23160b 100%);
  box-shadow: inset 0 0 0 1px #0a0704, inset 0 0 0 2px #a8801f, inset 0 0 0 3px #0a0704, inset 0 4px 8px rgba(0,0,0,0.5);
}
.hs-tfoot__income .hm-icon { image-rendering: pixelated; }
.hs-tfoot__income svg { display: block; }
/* The seven growth tiles, four over three, each a small bust socket with the
   number available under it - the reference's own "+45". */
.hs-tfoot__growth { display: grid; grid-template-columns: repeat(4, var(--hs-tile-w)); gap: 4px 4px; }
.hs-tgrow {
  display: flex; flex-direction: column; align-items: stretch; gap: 0;
  padding: 0; border: 0; cursor: pointer; color: #f6e2ae; background: none;
}
.hs-tgrow__socket {
  position: relative; width: var(--hs-tile-w); height: var(--hs-tile-h); overflow: hidden; isolation: isolate;
  background-color: #1a120a;
  box-shadow: inset 0 0 0 1px #0a0704, inset 0 0 0 2px #a8801f, inset 0 0 0 3px #0a0704, inset 0 5px 10px rgba(0,0,0,0.55);
}
.hs-tgrow__socket::before {
  content: ""; position: absolute; inset: 3px; z-index: 0; pointer-events: none;
  background-image:
    radial-gradient(80% 60% at 50% 22%, rgba(255,252,240,0.5) 0%, rgba(0,0,0,0) 70%),
    linear-gradient(180deg, var(--hs-sky-hi, #7d94b8) 0%, var(--hs-sky, #46597a) 58%, var(--hs-sky-lo, #1c1a16) 100%);
  box-shadow: inset 0 0 10px rgba(0,0,0,0.45), inset 0 -8px 10px rgba(0,0,0,0.30);
}
/* A BUST, not a full figure: the sprite is drawn at twice the pocket's
   height, seated at the top, and the pocket crops it at the waist - which is
   how the reference's painted busts fill their sockets, and why a 96px pocket
   no longer holds a 40px figure on a field of black. */
.hs-tgrow__socket .hm-icon { position: absolute; left: 50%; top: 3px; transform: translateX(-50%); image-rendering: pixelated; z-index: 1; }
/* min-height, not height: the reading is the window's cap floor tall (15
   rows plus its shadow, 20 CSS px under an 80% page) and a 13px row let the
   next tile paint over the bottom third of +17. The column grows instead. */
.hs-tgrow__n { display: flex; justify-content: center; align-items: center; min-height: 13px; padding-bottom: 1px; line-height: 0; color: #f6e2ae; }
.hs-tgrow__n svg { display: block; }
.hs-tgrow:hover .hs-tgrow__socket { filter: brightness(1.25); }
.hs-tgrow:disabled { cursor: default; }

/* --- the middle: two rows of seven busts, a braid, a status line ---------- */
.hs-tfoot__mid { display: flex; flex-direction: column; justify-content: center; gap: 5px; min-width: 0; padding: 6px 0 4px; }
.hs-tfoot__row { display: flex; align-items: stretch; gap: 4px; justify-content: center; }
.hs-tfoot__face {
  position: relative; flex: 0 0 auto;
  width: var(--hs-face); height: var(--hs-socket-h); overflow: hidden; isolation: isolate;
  display: flex; align-items: center; justify-content: center;
  padding: 0; border: 0; cursor: pointer; color: inherit;
  background-color: #1a120a;
  background-image: linear-gradient(180deg, #2c1e10 0%, #1a120a 100%);
  box-shadow: inset 0 0 0 1px #0a0704, inset 0 0 0 2px #b8903a, inset 0 0 0 3px #0a0704, inset 0 5px 10px rgba(0,0,0,0.55);
}
.hs-tfoot__face--none { cursor: default; }
.hs-tfoot__face .hm-icon { image-rendering: pixelated; flex: 0 0 auto; }
.hs-tfoot__face:hover:not(.hs-tfoot__face--none) { filter: brightness(1.2); }
/* The town's own flag at the head of the garrison row: a pennant in the
   owner's colour, which is what the reference hangs there. */
.hs-tfoot__flag {
  position: absolute; left: 50%; top: 10px; transform: translateX(-50%);
  width: 46px; height: 68px;
  background-color: var(--hs-velvet-hi, #6b4d15);
  background-image:
    repeating-linear-gradient(135deg, rgba(255,255,255,0.10) 0 6px, rgba(0,0,0,0) 6px 12px),
    linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(0,0,0,0) 40%, rgba(0,0,0,0.30) 100%);
  clip-path: polygon(0 0, 100% 0, 100% 78%, 50% 100%, 0 78%);
  box-shadow: inset 0 0 0 2px rgba(0,0,0,0.5);
}
.hs-tfoot__flagpole { position: absolute; left: 50%; top: 4px; width: 4px; height: 84px; transform: translateX(-50%); background: linear-gradient(90deg, #6b4d15, #e9cf92 50%, #6b4d15); z-index: 0; }
.hs-tfoot__flag { z-index: 1; }
.hs-town__rows .hs-tarmy { gap: 4px; }
.hs-town__rows .hs-tarmy__col { gap: 0; }
.hs-town__rows .hs-tarmy__head { display: none; }
.hs-town__rows .hs-tarmy__slots { gap: 4px; align-items: stretch; }
.hs-town__rows .hs-tcell { width: var(--hs-socket-w); gap: 0; }
.hs-town__rows .hs-tslot { width: var(--hs-socket-w); height: var(--hs-socket-h); }
/* The bust FILLS the socket: bottom-anchored 3px off the floor and drawn at
   the largest whole divisor the socket holds (see CAP_BOX). */
.hs-town__rows .hs-tslot__art { top: 3px; bottom: auto; }
/* The count is stamped INSIDE the socket, lower right, over the bust - the
   reference's own placement - in the HUD face with a keyline so it reads on
   whatever the creature's feet happen to be. */
.hs-town__rows .hs-tslot__count {
  left: auto; right: 4px; bottom: 3px; top: auto;
  min-width: 0; height: auto; padding: 0;
  display: flex; align-items: center; justify-content: flex-end;
  color: #fff4d6;
  background: none;
  box-shadow: none;
  z-index: 2;
}
.hs-town__rows .hs-tslot__count--empty { display: none; }
/* --- the sockets are the reference's, not a web grid's ------------------
   Re-measured on four references (Whitestone, Gravewatch, Darkhold and
   Edgewater): an EMPTY socket is a panel of marbled brown LEATHER inside the
   same dark-and-gilt bezel a filled socket has - the round-1 claim that the
   velvet shows through was wrong, and six bare velvet holes made the console
   read unfinished. An occupied socket is a painted portrait on a pale sky in
   the faction's own cast (\`bustSkyVars\`), the upper window of the figure
   fitted to the pocket (\`portraitFitBox\`). */
.hs-town__rows .hs-tcell .hs-tslot {
  background-color: #4a2f17;
  background-image:
    radial-gradient(60% 50% at 30% 25%, rgba(255,214,150,0.16) 0%, rgba(0,0,0,0) 70%),
    radial-gradient(50% 45% at 75% 70%, rgba(0,0,0,0.22) 0%, rgba(0,0,0,0) 70%),
    radial-gradient(28% 22% at 62% 30%, rgba(255,220,160,0.09) 0%, rgba(0,0,0,0) 100%),
    repeating-linear-gradient(97deg, rgba(255,230,180,0.05) 0 1px, rgba(0,0,0,0) 1px 5px),
    repeating-linear-gradient(13deg, rgba(0,0,0,0.10) 0 1px, rgba(0,0,0,0) 1px 7px),
    linear-gradient(180deg, #5a3a1d 0%, #48301a 45%, #3a2412 100%);
  box-shadow: inset 0 0 0 1px #0a0704, inset 0 0 0 2px #b8903a, inset 0 0 0 3px #3a2a10, inset 0 0 0 4px #0a0704, inset 0 0 10px rgba(0,0,0,0.45);
  filter: none;
}
/* A pale rim behind the figure: the one thing that keeps bone on bone-grey
   sky and black iron on dusk from fusing with the ground. One pixel, cream. */
.hs-town__rows .hs-tcell .hs-tslot .hs-tslot__art {
  filter: drop-shadow(0 0 1px rgba(255,244,214,0.55)) drop-shadow(1px 2px 1px rgba(0,0,0,0.55));
}
.hs-town__rows .hs-tcell .hs-tslot--empty::before { display: none; }
.hs-town__rows .hs-tcell .hs-tslot--empty::after { display: none; }
.hs-town__rows .hs-tcell .hs-tslot:not(.hs-tslot--empty)::before {
  inset: 3px;
  background-image:
    radial-gradient(80% 60% at 50% 22%, rgba(255,252,240,0.55) 0%, rgba(0,0,0,0) 70%),
    linear-gradient(180deg, var(--hs-sky-hi, #7d94b8) 0%, var(--hs-sky, #46597a) 58%, var(--hs-sky-lo, #1c1a16) 100%);
  background-size: auto, auto;
  background-repeat: no-repeat, no-repeat;
  box-shadow: inset 0 0 10px rgba(0,0,0,0.45), inset 0 -8px 10px rgba(0,0,0,0.30);
}
.hs-town__rows .hs-tcell .hs-tslot:hover:not(.hs-tslot--empty) { filter: brightness(1.18); }
.hs-town__rows .hs-tname { display: none; }
.hs-town__rows .hs-tbuy__price { display: none; }
/* The braid between the two rows: the reference's gilt filigree band. */
.hs-tfoot__braid {
  height: 10px; margin: 0 2px;
  background-color: rgba(0,0,0,0.30);
  background-image:
    radial-gradient(circle at 7px 5px, #e9cf92 0 1.5px, rgba(0,0,0,0) 2px),
    linear-gradient(135deg, rgba(0,0,0,0) 0 40%, #c9a24a 40% 60%, rgba(0,0,0,0) 60% 100%),
    linear-gradient(45deg, rgba(0,0,0,0) 0 40%, #c9a24a 40% 60%, rgba(0,0,0,0) 60% 100%);
  background-size: 14px 10px, 14px 10px, 14px 10px;
  background-repeat: repeat-x, repeat-x, repeat-x;
  box-shadow: inset 0 1px 0 rgba(0,0,0,0.7), inset 0 -1px 0 rgba(255,226,170,0.16);
}
.hs-tfoot__status {
  display: flex; align-items: center; justify-content: center; height: 22px; margin: 0 2px;
  color: #f4e4bc; line-height: 0; white-space: nowrap; overflow: hidden;
  background-color: rgba(8,5,2,0.42);
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,226,170,0.10);
}
.hs-tfoot__status svg { display: block; }

/* --- the icon column on the right ---------------------------------------- */
.hs-tfoot__side { display: grid; grid-template-columns: repeat(2, var(--hs-ibtn)); grid-auto-rows: var(--hs-ibtn); gap: 4px; align-content: center; padding: 6px 6px 4px 0; }
.hs-ibtn {
  position: relative; display: flex; align-items: center; justify-content: center;
  width: var(--hs-ibtn); height: var(--hs-ibtn); padding: 0; border: 0; cursor: pointer; color: #f2e2b8;
${reliefDecls("chamfer", "bronze", 2)}
}
.hs-ibtn:hover:not(:disabled) { filter: brightness(1.18); }
.hs-ibtn:active:not(:disabled), .hs-ibtn[aria-pressed="true"] {
${reliefDecls("recess", "bronze", 2)}
}
.hs-ibtn[aria-pressed="true"] .hs-glyph__ink { fill: #ffd25a; }
.hs-ibtn:disabled { cursor: not-allowed; filter: saturate(0.3) brightness(0.7); }
/* Three pixels into the column's own padding on each side: the LEAVE label
   at the 1080p floor is 80px in a 104px plate at 100% and 100 CSS px under
   an 80% page, and the 4px inset on both flanks (WIDE_PLATE_INSET, which
   wideLabelFit measures against) needs the extra six. */
.hs-ibtn--wide { grid-column: 1 / span 2; width: auto; margin: 0 -3px; padding: 0 4px; }
.hs-ibtn--gold { color: #2a1c05;
${reliefDecls("chamfer", "bronze", 3)}
  background-color: #b9932f;
  background-image: linear-gradient(180deg, #f9ecb8 0%, #dcb85e 30%, #a97f1c 70%, #6d5210 100%);
}
.hs-ibtn--gold .hs-glyph__ink { fill: #3c2a06; }
.hs-ibtn--gold .hs-glyph__shade { fill: rgba(255,252,225,0.66); }
.hs-ibtn__text { display: flex; line-height: 0; margin-left: 4px; }
.hs-ibtn__text--alone { margin-left: 0; }
.hs-ibtn__text svg { display: block; }
/* The building picture on a plate: a painted frame, smooth, on a shallow
   sky recess so the plate reads as a framed picture and not a sticker. */
.hs-ibtn__art {
  display: flex; align-items: center; justify-content: center;
  width: calc(var(--hs-ibtn) - 8px); height: calc(var(--hs-ibtn) - 8px);
  background-image:
    radial-gradient(80% 60% at 50% 20%, rgba(255,250,236,0.45) 0%, rgba(0,0,0,0) 70%),
    linear-gradient(180deg, #a9bdd3 0%, #6d7f9a 60%, #2b2418 100%);
  box-shadow: inset 0 0 0 1px #0a0704, inset 0 0 6px rgba(0,0,0,0.5);
}
.hs-ibtn__art .hm-icon { image-rendering: auto; filter: drop-shadow(0 1px 1px rgba(0,0,0,0.6)); }
.hs-ibtn__check { display: block; }
.hs-ibtn--wide .hs-ibtn__check { width: 26px; height: 26px; }

/* --- the resource strip along the foot ----------------------------------- */
.hs-tfoot__strip {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  height: 34px; margin-top: 4px; padding: 0 10px;
  background-color: #2c1c0f;
  background-image: linear-gradient(180deg, #3d2716 0%, #2c1c0f 100%);
  box-shadow: inset 0 0 0 1px #0a0704, inset 0 0 0 2px #7a5a1c, inset 0 0 0 3px #0a0704;
}
.hs-tfoot__strip .hs-purse { gap: 18px; }
.hs-tfoot__date { display: flex; align-items: center; color: #efe2c2; line-height: 0; white-space: nowrap; }
.hs-tfoot__date svg { display: block; }
.hs-town__fold {
  position: absolute;
  z-index: 6;
  top: 8px;
  right: 8px;
  min-height: 44px;
  padding: 0 12px;
  border: 0;
  cursor: pointer;
  color: #efe2c2;
  background-color: #2c1c0f;
  box-shadow: inset 0 0 0 1px #0a0704, inset 0 0 0 2px #7a5a1c;
}
.hm-root--phone .hs-town__rows {
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
  max-height: none;
  flex: 0 0 auto;
}
/* Seven 78px compact sockets need ~640px in the middle column. A landscape
   phone has ~500px left after the crest and the icon plates, which is why
   only five pockets showed and the rest were clipped with no scrollbar —
   the mid track shrinks (minmax 0 1fr) and the row never grew the scrollport.
   Shrink the pockets, and let the row scroll if it still overruns. */
.hm-root--phone .hs-town__rows .hs-tfoot {
  --hs-socket-w: 44px;
  --hs-socket-h: 40px;
  --hs-tile-w: 36px;
  --hs-tile-h: 28px;
  --hs-face: 36px;
  --hs-ibtn: 32px;
  gap: 0 4px;
  padding: 4px 4px 2px;
}
.hm-root--phone .hs-tfoot__mid {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  min-width: 0;
  padding: 2px 0;
}
.hm-root--phone .hs-tfoot__row,
.hm-root--phone .hs-town__rows .hs-tarmy,
.hm-root--phone .hs-town__rows .hs-tarmy__col,
.hm-root--phone .hs-town__rows .hs-tarmy__slots {
  flex-wrap: nowrap;
  min-width: max-content;
  width: max-content;
}
.hm-root--phone .hs-tfoot__crest { width: 32px; height: 32px; }
.hm-root--phone .hs-tfoot__flag { width: 22px; height: 32px; top: 4px; }
.hm-root--phone .hs-tfoot__flagpole { height: 40px; }
.hm-root--phone .hs-ibtn--wide .hs-ibtn__text { display: none; }
/* HTML5 drag does not run on iOS Safari, and touch-action:manipulation
   on every phone button would let the army-row scroller steal the gesture.
   The socket owns the finger; a tap still clicks (select / inspect). */
.hm-root--phone .hs-town__rows .hs-tslot {
  touch-action: none;
  -webkit-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
  -webkit-user-drag: none;
}
.hm-root--phone .hs-town__rows .hs-tslot--dragging {
  opacity: 1;
  outline: 2px solid var(--hm-gold-3, #d9b25a);
  outline-offset: -2px;
}

/* --- 1366x768: the same console on two thirds of the width -------------- */
@media (max-width: 1600px) {
  .hs-screen .hs-town__rows { padding: 4px 8px 4px; }
  .hs-town__rows .hs-tfoot {
    --hs-socket-w: 78px;
    --hs-socket-h: 62px;
    --hs-tile-w: 62px;
    --hs-tile-h: 44px;
    --hs-face: 66px;
    --hs-ibtn: 34px;
    gap: 0 8px; padding: 5px 6px 4px;
  }
  .hs-tfoot__left { gap: 4px; padding: 4px 0 2px 4px; }
  .hs-tfoot__crest { width: 50px; height: 50px; }
  .hs-tfoot__name { height: 22px; }
  .hs-tfoot__mark, .hs-tfoot__income { width: 32px; height: 32px; }
  .hs-tfoot__income { width: auto; padding: 0 5px 0 3px; gap: 4px; }
  .hs-tfoot__mid { gap: 3px; padding: 4px 0 2px; }
  .hs-tfoot__braid { height: 8px; }
  .hs-tfoot__status { height: 18px; }
  .hs-tfoot__flag { width: 32px; height: 46px; top: 8px; }
  .hs-tfoot__flagpole { height: 58px; }
  .hs-tfoot__side { gap: 3px; padding: 4px 4px 2px 0; }
  .hs-ibtn--wide .hs-glyph { display: none; }
  .hs-ibtn--wide .hs-ibtn__check { width: 20px; height: 20px; }
  .hs-ibtn__text { margin-left: 0; }
  .hs-tgrow__n { min-height: 14px; }
  .hs-tfoot__strip { height: 28px; margin-top: 3px; padding: 0 8px; }
  .hs-tfoot__strip .hs-purse { gap: 10px; }
  .hs-tfoot__strip .hs-purse__item { gap: 3px; }
}

/* --- pushing the backdrop back behind the town --------------------------
   The painted backdrop and the buildings standing in front of it are the same
   art at the same saturation, and on the swamp town that is a green haze in
   which nothing separates: measured, a Fenmire building pixel differed from
   the backdrop behind it by 35.4 mean luma against Castle's 65.3. So the
   backdrop loses chroma and contrast and gains the sky. The other half - the
   buildings gaining a little of both - rides on the --hs-roof variable, set by
   TownBuilding, rather than on a second rule here: redeclaring the building
   sprite's filter at a higher specificity would also outrank the hover,
   focus-visible and ghost rules that share it, and a legibility fix is not
   allowed to cost the screen its affordances. */
.hs-town__canvas .hs-town__bg { filter: var(--hs-bg-tone, brightness(1)); }
/* --- what the far grade owes the backdrop -------------------------------
   DEPTH.far in TownScreen is a black multiply over the distance. It leaves the
   distance's hue and its SATURATION untouched, which is what a multiply does,
   and it takes 17% of its absolute chroma with the 17% of value it removes,
   which is also what a multiply does - and absolute far/near chroma is the
   ratio the round before this one was fitted on. Measured over the twelve with
   the grade in and nothing given back: 0.664 against the classic's 0.752, from 0.785.

   So each town's backdrop is saturated by exactly 1/(1-far): the far band ends
   at the chroma the airlight left it at and the value the grade takes it to.
   It has to be here rather than beside DEPTH because a filter cannot be
   applied to a blend scrim - the scrim has no pixels of its own - and it is on
   the backdrop because the backdrop is what the far band is nearly all of.
   Prismgate takes no far grade and so appears nowhere below.

   Fenmire is the one that is not just a compensation. Its far band measured
   17.80 mean chroma, the lowest of the twelve, and a blind critic found no
   value and no hue separating the cathedral from the fog behind it.
   saturate(0.52) is what put it there: the backdrop was desaturated so the
   buildings would stand off it, which is a chroma answer to a value question.
   Edgewater's far band sits 11 luma above its own foreground and ours sat 48.
   So the desaturation goes entirely - Fenmire takes the same 1/(1-0.18) as
   every other town on the cap and nothing else - and the separation moves to
   value, where the far grade now carries it: measured, its far band went from
   48 luma above its own foreground to 22, and its distance from 10 to 22 units
   bluer than its foreground. brightness and contrast stay: a swamp reads flat,
   not dim. */
.hs-town__canvas[data-faction="sunspire"]     { --hs-bg-tone: saturate(1.20); }
.hs-town__canvas[data-faction="greenmantle"]    { --hs-bg-tone: saturate(1.15); }
.hs-town__canvas[data-faction="skyreach"]      { --hs-bg-tone: saturate(1.08); }
.hs-town__canvas[data-faction="emberpit"]    { --hs-bg-tone: saturate(1.06); }
.hs-town__canvas[data-faction="gravewatch"] { --hs-bg-tone: saturate(1.22); }
.hs-town__canvas[data-faction="deepvault"]    { --hs-bg-tone: saturate(1.11); }
.hs-town__canvas[data-faction="ironhorde"] { --hs-bg-tone: saturate(1.16); }
.hs-town__canvas[data-faction="fenmire"] {
  --hs-bg-tone: saturate(1.22) brightness(1.10) contrast(0.82);
}
.hs-town__canvas[data-faction="saltreach"]       { --hs-bg-tone: saturate(1.04); }
.hs-town__canvas[data-faction="cogworks"]    { --hs-bg-tone: saturate(1.22); }
.hs-town__canvas[data-faction="frostmark"]    { --hs-bg-tone: saturate(1.22); }

/* --- the town's name, at the size the largest type on the bar deserves ---
   The plate grows to hold a cap-11 face at magnification 2 plus a cap-9 line
   under it - and the sub-line is ELEVEN rows, not nine, because a bitmap line
   box now holds its own descenders instead of hanging them outside the element.
   22 + 3 + 11 is 36, the chamfer owes two rows top and bottom, and 44 leaves
   two rows of air either side of that. At 38 the tail of the y in "Day 4" was
   painted straight through the plate's own bottom lip on every town.
   The bar grows with it, from 46 to 50: the plate is the tallest thing on the
   bar and a plate that fills its bar to within a pixel reads as a plate that
   has burst out of it. Four rows of a 720-row painting is the whole cost. Horizontal padding comes down from 14 to 9 because
   a stamped face is wider than the 16px serif it replaces and the bar at 1366
   is packed edge to edge; the same reason the sub-line sets at magnification 1.
   line-height 0 on both rows because an SVG in a flex column otherwise gets a
   text line box it does not use. */
.hs-townbar .hs-nameplate { height: 44px; padding: 0 9px; gap: 3px; }
.hs-townbar .hs-nameplate__name,
.hs-townbar .hs-nameplate__sub { line-height: 0; text-shadow: none; }
.hs-townbar .hs-nameplate__name { color: #f6e3ae; }
.hs-townbar .hs-nameplate__sub { color: #b9a271; letter-spacing: 0; }
.hs-townbar .hs-nameplate svg { display: block; }
/* The stamped label is one SVG in a flex row and needs nothing from the line
   box - but line-height:0 must NOT be set on .hs-tlabel or .hs-thint. Those
   two also contain the Dismiss button, whose own text is still typeset: with a
   zero line box its 10px label overflowed a 2px-tall box and the chamfer's lit
   lip drew straight through the middle of the word, so DISMISS shipped struck
   through. Measured on the Fenmire bar: rows 946-947 were a solid 224 across
   the whole button, with the glyphs above and below them. */
.hs-townbar .hs-carve__text { display: inline-flex; align-items: center; line-height: 0; }

/* --- the tonal grade ----------------------------------------------------
   Measured against the ten faction-matched references, the one thing that
   separates our painting from the classic's is not its value RANGE - six hand-picked
   buildings a side of the Fenmire pair came to 130.6 against 145.1, ten per
   cent - it is where that range sits. Our paintings never reach black: p1 luma
   18-22 in every daylit town against the classic's 0.6-13.9, and a dark-pixel
   fraction of 7.0% against Saltreach's 25.4%, 13.8% against Fenmire' 37.8%. The
   buildings already carry the contrast; the frame is exposed about a stop and
   a half hot with the shadow end filled in.

   FOUR layers take the same grade or the backdrop, the ground, the scenery and
   the buildings drift into four different times of day. Three of them are
   redeclared below at one extra level of specificity - .hs-town is an
   ancestor the shared sheet does not use - because the chains they extend live
   in styles.ts; the fourth rides on --hs-roof, which TownBuilding
   already composes.

   BRIGHTNESS BEFORE CONTRAST, and that order is the whole highlight argument.
   contrast(c) with c>1 pushes white past 1.0 and every filter function
   clamps its own output, so contrast(1.3) brightness(0.75) clips the top
   FIRST and then scales the clipped white down - which is how a linear grade
   ends up with bright>200 at nought. Reversed, brightness(b) with b<=1
   cannot clip anything, and the contrast that follows clamps at both ends at
   once: a hard S rather than a ramp with its head cut off. Measured over the
   ten graded towns the smallest bright>200 fraction after grading is 0.00%
   on Fenmire against its reference's 0.31%, and the largest change in the
   other direction is Tower's 23.6% -> 30.2% against 30.0%.

   Per FACTION, not per country. Five factions stand on grass and Castle is the
   one of them already at parity with its reference (local range 126.1 vs
   124.9, dark 7.30% vs 6.01%, bright 6.74% vs 7.24%), so it is absent here and
   must stay absent. Deepvault is absent for the same reason - 22.9 foreground sd
   against Darkhold's 31.7, and it won its blind round.

   Every pair is the least-squares fit of (mean, p1, dark<40, bright>200)
   against that faction's own reference, searched over the shipped 1080p
   screenshot's own histogram. Fitted TWICE: the first fit was made against the
   pre-occlusion layout, and a front band drawn at 4/3 with two foreground
   occluders in it changes the histogram it was fitted to, so each value was
   re-solved against a shot of the new layout with its own first-round grade
   numerically inverted out. Two of them are deliberately short of their fit -
   Emberpit at 0.74 where the fit wanted 0.66, and Ironhorde at 1.16 where it
   wanted 1.19 - because matching those two exactly costs more in foreground
   flatness and blown highlights than the mean it buys. */
.hs-town .hs-town__canvas .hs-town__bg {
  filter: var(--hs-bg-tone, brightness(1)) var(--hs-grade, brightness(1));
}
.hs-town .hs-town__ground {
  filter: var(--hs-ground-tint, brightness(1)) var(--hs-grade, brightness(1));
}
.hs-town .hs-town__prop .hs-town__art {
  filter: var(--hs-prop-tint, brightness(1)) var(--hs-grade, brightness(1))
    drop-shadow(6px 4px 0 rgba(26,19,9,0.42))
    drop-shadow(2px 1px 0 rgba(20,14,6,0.5));
}
.hs-town__canvas[data-faction="greenmantle"]    { --hs-grade: brightness(1.06) contrast(1.02); }
.hs-town__canvas[data-faction="skyreach"]      { --hs-grade: brightness(1.12) contrast(1.10); }
.hs-town__canvas[data-faction="emberpit"]    { --hs-grade: brightness(0.74) contrast(1.02); }
.hs-town__canvas[data-faction="gravewatch"] { --hs-grade: brightness(0.80) contrast(1.00); }
.hs-town__canvas[data-faction="ironhorde"] { --hs-grade: brightness(1.16) contrast(1.04); }
.hs-town__canvas[data-faction="fenmire"]   { --hs-grade: brightness(0.68) contrast(1.04); }
.hs-town__canvas[data-faction="prismgate"]    { --hs-grade: brightness(0.89) contrast(1.14); }
.hs-town__canvas[data-faction="saltreach"]       { --hs-grade: brightness(0.94) contrast(1.28); }
/* No faction-matched reference ships for these two, so the target is the ten
   references' own population - p1 near 6, a dark fraction near 21% - rather
   than one picture. Frostmark takes half the cut its histogram asked for: fitted
   straight to that target it wanted brightness(0.71), which took its
   bright>200 from 17.8% to nought, and a town with real speculars in it is
   not one to flatten for a population mean. */
.hs-town__canvas[data-faction="cogworks"]    { --hs-grade: brightness(0.86) contrast(1.12); }
.hs-town__canvas[data-faction="frostmark"]    { --hs-grade: brightness(0.90) contrast(1.14); }

/* --- the foreground occluder --------------------------------------------
   The two props nearer than the front band. They are the deepest plane in the
   frame, so they take the near light and the warm ground bounce that go with
   being in the room rather than in the picture - and one of them needed more
   than that.

   The rim is a light drop-shadow thrown UP AND LEFT, which is where this
   screen's sun is. A drop-shadow in a light colour offset AGAINST the light
   paints a lit lip along the silhouette it clears, and that is the one thing
   a filter can add to a sprite that the sprite does not already contain -
   which is why the other half of the answer to "no chroma, no rim light" is
   in townScenery rather than here. A filter cannot invent chroma: saturate()
   scales what is there, and on a grey column what is there is nought.

   Set as a variable rather than a rule per faction because the occluder's
   chain already carries --hs-prop-tint and --hs-grade, and a second rule at
   higher specificity would outrank both. */
.hs-town .hs-town__prop--fg .hs-town__art {
  filter: var(--hs-fg-tint, brightness(1)) var(--hs-prop-tint, brightness(1))
    var(--hs-grade, brightness(1))
    drop-shadow(6px 4px 0 rgba(26,19,9,0.42))
    drop-shadow(2px 1px 0 rgba(20,14,6,0.5))
    var(--hs-fg-rim, drop-shadow(0 0 0 rgba(0,0,0,0)));
}
/* The two counter-rotations, and they are keyed by COUNTRY rather than by
   faction because what they undo is keyed by country.

   --hs-prop-tint exists to put a green pine or a brown rock into the country
   it is standing in, and it does that with a large hue-rotate: 168deg on snow,
   214deg on cave. Both are right for the props the atlas offers that country
   and both are wrong for the one prop chosen because it ALREADY has that
   country's colour. Rotated 214deg, Deepvault's violet crystal came back
   emerald; rotated 168deg, Tower's conifers came back lavender - "in a picture
   whose entire chromatic idea is cold blue against warm gold, they are the
   only mid-warm mass, they are dead centre, and they are the largest single
   silhouette in the lower half", which a blind critic found without knowing
   what he was looking at.

   Filters compose left to right and hue-rotate is a rotation, so an equal and
   opposite one in front of it cancels exactly. What is left is the saturation
   and brightness, deliberately not cancelled: the near occluder keeps its own
   HUE and still takes its country's light. */
.hs-town__canvas[data-ground="cave"] {
  --hs-fg-tint: hue-rotate(-214deg) saturate(1.2) brightness(1.35);
  /* Narrower than a stone town's rim: a facet has its own edges, and a 2px
     halo over them reads as a sticker. */
  --hs-fg-rim: drop-shadow(-1px -1px 0 rgba(206,188,255,0.40));
}
.hs-town__canvas[data-ground="snow"] {
  --hs-fg-tint: hue-rotate(-168deg) saturate(0.82) brightness(0.88);
  --hs-fg-rim: drop-shadow(-1px -1px 0 rgba(222,236,255,0.34));
}
/* Gravewatch' standing stone and Ironhorde's share one source, and a first
   attempt gave each a tint and a rim to tell them apart. Both are gone and the
   reason is on the shot: the menhir is light grey granite, the near light is a
   gamma lift, and a brightness on top of it took Ironhorde's two stones to a
   pale blue-white that read as ice in a red desert. Two towns drawing one
   silhouette is a smaller fault than one town drawing the wrong material. */

/* --- the cast shadow ----------------------------------------------------
   The thrown parallelogram under every sprite; see SUN_CAST in TownScreen. A
   flat fill, not a gradient: the two contact ellipses drawn over it carry the
   soft end, and a second soft shape here only smears the first. */
.hs-town .hs-town__cast { fill: #16100a; fill-opacity: 0.22; }

/* --- the one value anchor a painting needs ------------------------------
   Greenmantle's bright>250 fraction is 0.11% against Seiliedh's 8.12% and its
   p99 luma is 197 against 250 - it has no highlight anywhere, and no global
   curve can invent one from a histogram that stops at 197 without blowing the
   whole picture out. So the anchor is drawn: a shaft of sun through the
   canopy, screened over the finished frame.

   It is a direct child of .hs-town__canvas, drawn last. mix-blend-mode
   inside the paths SVG had nothing but its own siblings to blend with, because
   that SVG carries a transform and a transform is a stacking context; the
   canvas is the outermost context that contains the backdrop, the ground and
   every building, so a screen blend here reaches all of them - which is also
   what makes Emberpit's fires light the buildings standing over them instead of
   glowing to themselves. */
.hs-town__glow {
  position: absolute;
  inset: 0;
  pointer-events: none;
  mix-blend-mode: screen;
  background-image: var(--hs-glow, none);
  background-repeat: no-repeat;
  /* Every layer of one background shares one size, which is exactly what a
     shaft of sun wants: it has to STOP before it reaches the promenade. Drawn
     full height, Greenmantle's shaft lay over the market roofs and the cobble in
     front of them and read as smoke rather than as light coming through a gap
     in the canopy. Sixty-six per cent puts its foot on the front band's roof
     line. */
  background-size: var(--hs-glow-size, 100% 100%);
  /* ...and a background box that stops has an EDGE where it stops. Measured on
     the shipped frame: at 1080p, canvas row 476 - which is 66% of 720 - 62% of
     the columns in the window stepped more than 4 luma with a mean step of
     -17.3, a straight machine line about 370px wide lying across painted
     hills, and it was the first thing a blind critic named on that screen. The
     shaft's own gradient cannot carry the fade, because the 107deg gradient
     line runs across the box rather than down it, so the fade is a mask on the
     ELEMENT: the mask's percentages and the background box's are the same
     percentages, so the light is already out by the row the box ends on. Per
     faction, keyed to the same variable as the size, because Emberpit's and
     Fenmire' ground fires live at 90% of the element and a shared mask would
     put them out. */
  -webkit-mask-image: var(--hs-glow-mask, none);
  mask-image: var(--hs-glow-mask, none);
}
.hs-town__canvas[data-faction="greenmantle"] {
  --hs-glow-size: 100% 66%;
  --hs-glow-mask: linear-gradient(180deg, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 38%,
    rgba(0,0,0,0.55) 54%, rgba(0,0,0,0) 66%);
  --hs-glow:
    linear-gradient(107deg, rgba(0,0,0,0) 26.5%, rgba(255,248,218,0.94) 30%,
      rgba(255,244,198,0.34) 34.5%, rgba(0,0,0,0) 37%),
    linear-gradient(107deg, rgba(0,0,0,0) 38%, rgba(255,246,206,0.62) 40.5%,
      rgba(255,246,206,0.20) 42%, rgba(0,0,0,0) 44.5%),
    radial-gradient(38% 30% at 31% 3%, rgba(255,250,222,0.86) 0%, rgba(255,244,196,0.22) 44%,
      rgba(0,0,0,0) 72%);
}
/* Emberpit's foreground band has a luma sd of 18.0 against the classic Emberpit
   reference's 46.8 - the flattest foreground in our twelve, measured against
   the least flat of the ten references. Theirs pours a lava river across the
   plane; ours had isolated braziers that lit nothing, because every fire on
   the screen was inside a sprite. Four pools on the ground plane, screened,
   so the plane itself has a value range and the buildings over it take light
   off it. */
/* Fenmire' foreground band has a luma sd of 18.0 against Edgewater's 37.3, and
   the mechanism is the same one as Emberpit's: a plane with no light on it. Its
   grade is the deepest of the twelve - brightness(0.68) - and a bog that dark
   is CORRECT, because Edgewater's water is near black too. What Edgewater has
   on top of it is lily pads, white flowers and a plank walk catching the sky,
   and ours had nothing brighter than wet cobble. Four pale sheens where the
   standing water would be, and one warm one at the tavern's own doorway. */
.hs-town__canvas[data-faction="fenmire"] {
  --hs-glow:
    radial-gradient(19% 6% at 16% 96%, rgba(196,214,178,0.40) 0%, rgba(150,170,132,0.12) 46%,
      rgba(0,0,0,0) 80%),
    radial-gradient(15% 5% at 44% 90%, rgba(206,222,186,0.34) 0%, rgba(0,0,0,0) 74%),
    radial-gradient(17% 5% at 70% 97%, rgba(190,210,172,0.36) 0%, rgba(0,0,0,0) 76%),
    radial-gradient(13% 4% at 88% 88%, rgba(200,216,180,0.30) 0%, rgba(0,0,0,0) 72%),
    radial-gradient(12% 8% at 9% 78%, rgba(255,186,96,0.30) 0%, rgba(0,0,0,0) 78%);
}
.hs-town__canvas[data-faction="emberpit"] {
  --hs-glow:
    radial-gradient(28% 9% at 24% 92%, rgba(255,176,60,0.92) 0%, rgba(226,86,20,0.42) 42%,
      rgba(0,0,0,0) 78%),
    radial-gradient(22% 7% at 58% 86%, rgba(255,164,52,0.80) 0%, rgba(214,76,18,0.36) 44%,
      rgba(0,0,0,0) 80%),
    radial-gradient(30% 8% at 84% 95%, rgba(255,186,72,0.86) 0%, rgba(226,86,20,0.38) 40%,
      rgba(0,0,0,0) 76%),
    radial-gradient(40% 14% at 50% 74%, rgba(196,64,16,0.34) 0%, rgba(0,0,0,0) 72%);
}
`;

/** Appends the town's own rules for as long as a town screen is mounted. */
export function TownStyles(): null {
  useStyleSheet(TOWN_STYLE_ID, TOWN_CSS, StyleLayer.screen);
  return null;
}

// ------------------------------------------------------------------ glyphs

/**
 * The marks, as pixel figures rather than as vector outlines.
 *
 * They were 16-unit paths stroked at 1.4 units and drawn down to 9-13 CSS
 * pixels, which puts the stroke on about one device pixel with the rasteriser
 * free to spread it over two - and in near-black on dark bronze. The five
 * plaque marks, the three section marks and the coin on every price plate all
 * arrived as soft grey smudges, and they were the only anti-aliased shapes in
 * a frame whose type, plates and sprites are every one of them quantised.
 *
 * Seven rows of seven, `#` for ink. A figure this small has to be AUTHORED at
 * the size it is read at: a shape that survives being a silhouette is the only
 * kind that survives seven pixels, so each of these is one closed mass with at
 * most one counter in it, and none of them relies on a one-pixel stroke to
 * carry its reading. `.hs-glyph__ink` and `.hs-glyph__shade` in `styles.ts`
 * decide which of the two copies is the light one; on dark bronze the light
 * copy is the figure, on the gilt BUILD plate it is the shadow under it.
 */
const GLYPH: Readonly<Record<string, readonly string[]>> = Object.freeze({
  guild: ["...#...", "...#...", "#######", ".#####.", "..###..", ".##.##.", "##...##"],
  tavern: [".......", "#####..", "#######", "#####.#", "#######", "#####..", "......."],
  leave: [".......", "...##..", "..####.", "#######", "..####.", "...##..", "......."],
  // A mallet: a head the full width of the cell over a three-pixel haft. The
  // head used to be five wide over a three-wide haft, which at seven pixels is
  // a capital T and read as one.
  build: ["#######", "#######", "..###..", "..###..", "..###..", "..###..", "..###.."],
  // BUILT counts structures, so it takes a wall rather than the tool that
  // raises one. One mark served BUILT, BUILD and AUTO: OFF, which is a legend
  // that says nothing about which of the three a click does.
  keep: ["#.#.#.#", "#######", "###.###", "#######", "#.###.#", "#######", "......."],
  hero: ["..###..", "..###..", "...#...", ".#####.", "#######", "#######", "#######"],
  // A heater shield: straight top, straight sides, one point. Solid to the
  // waist and then tapering it read as a heart at seven pixels - which is the
  // universal HEALTH mark, and this one stands for defence in three places. A
  // rim rather than a solid mass is what separates the two silhouettes.
  shield: ["#######", "#.....#", "#.###.#", "#.###.#", ".#####.", "..###..", "...#..."],
  scroll: ["#######", "#.....#", "#.###.#", "#.....#", "#.###.#", "#.....#", "#######"],
  coin: ["..###..", ".#####.", "#######", "#######", "#######", ".#####.", "..###.."],
  // A covered waggon on two wheels: creatures on the road with nobody driving.
  caravan: [".......", "..####.", ".#####.", "#######", "#######", ".#...#.", "......."],
  // The five the visiting hero's card reads with. Defence borrows `shield`,
  // which is the same reading it carries on the FORT plaque and over the
  // GARRISON label - a mark that means two things on one screen is worse than
  // one that means the same thing three times.
  sword: ["....###", "...###.", "..###..", ".####..", "####...", "###....", "##....."],
  // Spell power is a struck spark, not a stone. Drawn as a faceted lozenge it
  // was the gems resource medallion at a third of the size, two bars from the
  // treasury that carries the real one.
  orb: ["...#...", "...#...", "..###..", "#######", "..###..", "...#...", "...#..."],
  // Two pages rising off a common base. A solid slab with two notched top
  // corners read as a folder tab.
  book: [".......", "##...##", "###.###", "###.###", "###.###", "#######", "......."],
  boot: [".##....", ".##....", ".##....", ".##....", ".###...", "#####..", "#######"],
  // A neck is what makes a vessel a vessel: without one this was a blob that
  // could as easily have been a shield or a gem.
  flask: ["..###..", "...#...", "...#...", "..###..", ".#####.", "#######", ".#####."],
});

/**
 * The struck highlight, for the marks that are supposed to be metal.
 *
 * The shadow copy below sits one pixel down and right of every figure, which is
 * the rim shadow at five o'clock; what a coin also needs is the light the rim
 * catches at ten. Without it the most repeated mark on the screen - twenty-one
 * price plates and the INCOME plaque - is a flat cream octagon with a dark lip,
 * which is a pill and not a coin. Only the marks that name metal carry one; a
 * highlight on a book or a boot would be a specular on cloth.
 */
const GLYPH_LIT: Readonly<Record<string, readonly string[]>> = Object.freeze({
  coin: [".......", "..##...", ".#.....", ".......", ".......", ".......", "......."],
});

/** Glyph pixels a mark is authored on. The box is one wider, for the shadow. */
const GLYPH_GRID = 7;

/**
 * One `<path>` per figure, as a run of rectangles.
 *
 * Runs rather than one rect per lit pixel: a 7x7 figure is up to 49 subpaths
 * drawn twice on every plaque, button and price plate on the screen, and the
 * horizontal run is free to compute and cuts that by about four.
 */
function glyphPath(rows: readonly string[]): string {
  let d = "";
  rows.forEach((row, y) => {
    let x = 0;
    while (x < row.length) {
      if (row.charAt(x) !== "#") { x += 1; continue; }
      let run = 1;
      while (x + run < row.length && row.charAt(x + run) === "#") run += 1;
      d += `M${String(x)} ${String(y)}h${String(run)}v1h-${String(run)}z`;
      x += run;
    }
  });
  return d;
}

const GLYPH_PATH: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(Object.entries(GLYPH).map(([k, rows]) => [k, glyphPath(rows)])),
);

const GLYPH_LIT_PATH: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(Object.entries(GLYPH_LIT).map(([k, rows]) => [k, glyphPath(rows)])),
);

export function Glyph(props: { readonly name: string; readonly size?: number }): JSX.Element {
  const d = GLYPH_PATH[props.name] ?? GLYPH_PATH["shield"]!;
  // A WHOLE cell, and `size` is a request rather than a measurement: a 7-pixel
  // figure drawn at 1.6 pixels a cell is the resampling the bitmap face and
  // `fitSpriteBox` both exist to remove. The box is eight cells because the
  // shadow copy sits one pixel down and right of the figure.
  const cell = Math.max(1, Math.floor((props.size ?? 16) / (GLYPH_GRID + 1)));
  const box = (GLYPH_GRID + 1) * cell;
  const lit = GLYPH_LIT_PATH[props.name];
  return (
    <svg className="hs-glyph" width={box} height={box}
      viewBox={`0 0 ${String(GLYPH_GRID + 1)} ${String(GLYPH_GRID + 1)}`}
      shapeRendering="crispEdges" aria-hidden="true" focusable="false">
      <path d={d} className="hs-glyph__shade" transform="translate(1,1)" />
      <path d={d} className="hs-glyph__ink" />
      {lit === undefined ? null : <path d={lit} className="hs-glyph__spec" />}
    </svg>
  );
}

// ----------------------------------------------------------------- controls

/** A stamped brass plaque: one reading, engraved. */
export function Plaque(props: {
  readonly glyph?: string;
  readonly label: string;
  readonly value: string;
  readonly title?: string;
}): JSX.Element {
  // The five plaques share a strip with the treasury and three buttons, and at
  // 1366 that strip is 554px narrower with the same content in it. The reading
  // steps down a whole magnification rather than being squeezed to a fraction
  // of one, which is what would put it back on a half pixel.
  const scale = useBitmapScale(2);
  return (
    <span className="hs-plaque" title={props.title ?? `${props.label}: ${props.value}`}>
      {props.glyph === undefined ? null : <Glyph name={props.glyph} size={16} />}
      <span className="hs-plaque__body">
        {/* The caption is stamped in the same face as the reading below it, at
            magnification 1 - and that is what stopped the strip overflowing at
            1366.

            Measured on the shipped laptop shot the strip had 400px of room and
            wanted 430, so BUILT rendered as "BUI" over "33 O". The reading
            could not give the 30px back: `useBitmapScale(2)` has already
            stepped every value down to magnification 1 at this width, and the
            step below a whole magnification is a fraction of one, which is the
            resampling the bitmap face exists to remove. The CAPTION could. The
            two widest plaques are wide because of their caption, not their
            reading - DEFENCE and RECRUITS cost 55px and 62px set in small caps
            at 0.16em tracking, against 43px and 47px stamped - and five
            captions plus the padding trim below pay the 30px back with ~40px
            to spare, so a longer fort name or a five-figure income cannot
            re-open it.

            The three cheaper answers were all worse. Shortening RECRUITS, or
            cutting the caption line and leaving the glyph, buys more room by
            making a readout that no longer says what it is - "98" beside a
            small figure is not a reading. Wrapping the row costs a second
            46px band out of the painting. This one keeps all five captions,
            all five readings and one 46px bar, and it retires the "serif
            caption beside a bitmap numeral" item on the same plate. */}
        {/* Set in caps by this call site, not by the face.
            The bitmap face folded lowercase up to its capital until it grew a
            lowercase of its own, so every caption on this bar used to arrive in
            caps by accident. It is a LABEL - the same class of thing as the
            GARRISON, VISITING ARMY and FOR SALE labels beside it and the MAGE
            GUILD / TAVERN / LEAVE plates above them, all of which are small
            caps by CSS - so it asks for caps rather than inheriting whatever
            the face happens to do. The reading below it stays in its natural
            case, because "Castle" and "4,000/day" are readings, not labels. */}
        <span className="hs-plaque__label">
          <BitmapText scale={1}>{props.label.toUpperCase()}</BitmapText>
        </span>
        {/* A readout stamped into brass. Every plaque on this bar carries
            either a figure or a two-word state, which is exactly the class of
            text the bitmap face exists for; the plaque's own label above it is
            already small caps. */}
        {/* `reserveDescent`, because these five are a ROW of readings that
            must share a centre whatever they happen to contain. Without it
            `98` is a 7-row box and `4,000/day` a 9-row one, so the two plates
            centre their captions on two different lines - and the plate is
            sized on the taller of them, which is how the comma of `4,000` and
            the tail of `/day` came to be painted over the plate's own bottom
            rule while RECRUITS beside it had four rows to spare. */}
        <span className="hs-plaque__value">
          <BitmapText scale={scale} reserveDescent>{props.value}</BitmapText>
        </span>
      </span>
    </span>
  );
}

// ---------------------------------------------------------------- treasury

const RESOURCE_ORDER = [
  "gold", "wood", "ore", "mercury", "sulfur", "crystal", "gems",
] as const;

/**
 * The treasury, in the bar's own material.
 *
 * The shared strip draws its icons at 24px - one third of a 96px source - and
 * sets its numbers in the kit's monospace face. At 24 the gold pile reads as a
 * beehive and the ore as a grey lump, and the monospace was the only string on
 * the whole screen that was not the serif. Thirty-two is the next whole
 * divisor of 96 up, and each icon sits in the same bronze bezel as the
 * controls.
 *
 * The stock number and nothing else. A green `+4,000` stacked beside the gold
 * was a spreadsheet's idiom and not a game's - The classic's resource bar carries
 * the seven totals and the date, never a delta - and it was the field this
 * layout dropped first when the window narrowed, so the same screen showed
 * MORE information at 80% zoom than at 100%. The day's income is still on the
 * screen, on the Income plaque in the block to the left, and in this item's
 * own tooltip.
 */
export function TownTreasury(props: {
  readonly resources: Resources;
  readonly income?: Resources | null;
}): JSX.Element {
  const scale = useBitmapScale(2);
  const compact = useCompactTown();
  const icon = compact ? 24 : 32;
  return (
    <span className="hs-purse" role="list" aria-label="Treasury">
      {RESOURCE_ORDER.map((kind) => {
        const gain = props.income?.[kind] ?? 0;
        return (
          <span className="hs-purse__item" key={kind} role="listitem"
            title={`${titleCase(kind)}: ${groupDigits(props.resources[kind])}`
              + (gain === 0 ? "" : ` (+${groupDigits(gain)} a day)`)}>
            {/* The medallion is a socket in the bar, and the art is authored on
                its own opaque black ground - so it screens onto the socket's
                floor. Unblended, the icon repainted the recess back to #000
                and the treasury read as seven holes punched in the plate. */}
            <span className="hs-purse__socket">
              <MaybeIcon atlasKey={RESOURCE_ICON[kind]} size={icon} title={kind}
                className="hs-purse__icon hm-relief__inlay" />
            </span>
            <span className="hs-purse__body">
              {/* Stamped, not typeset. The treasury is the reading a player
                  checks most on this screen and it sits directly over the
                  bottom bar's bitmap counts; two faces for one kind of number
                  is what made the top bar read as a different program. */}
              <span className="hs-purse__count">
                <BitmapText face={romanFaceForStep(scale)} scale={1}>
                  {groupDigits(props.resources[kind])}
                </BitmapText>
              </span>
            </span>
          </span>
        );
      })}
    </span>
  );
}

// ---------------------------------------------------------- creature sockets

/**
 * Every creature in a row is drawn into one box and stood on one floor.
 *
 * The socket is 62x62 with the art bottom-anchored 5px off its floor, so this
 * is the rectangle inside its chamfer. It is a RECTANGLE and not a square on
 * purpose: `fitSprite` fits a frame by its LONGEST side into a square box, so
 * a 121x281 Lancer came out 47px tall beside a 167x177 Skyclaw at 44px and
 * half as wide again - the row read ragged and the wide creatures overhung
 * their own side walls, where `overflow: hidden` cropped them.
 * `fitSpriteBox` picks one whole divisor against BOTH axes, so every creature
 * in the row lands on one scale and inside its own walls, and bottom-aligning
 * them puts them on one floor.
 */
/**
 * The art box, wide window and narrow.
 *
 * The divisor is picked against the SHEET CELL now (see `sheetFitBox`), and the
 * box is not a dial either way - it is a threshold that a whole divisor steps
 * over. Wide is 54 of the 1080p socket's 68 rows and 58 of its 62 columns,
 * which is the plinth's 13 and the art's 1px seat taken off the height and the
 * chamfer's two lips off the width. 54 rather than 53 is worth a line: the
 * three sheets whose cell is 308-321 rows - Castle's monks and spearwardens among
 * them - divide by 6 at 54 and by 7 at 53, and a seventh is a fifth of the
 * creature. Narrow is unchanged, and both are checked against every front frame
 * in the shipped atlas: 552 of 552 land on the divisor `sheetFitBox` intends
 * and none of them overflows its box.
 */
export const CAP_BOX = Object.freeze({
  /* The wide socket grew from 62x68 to 88x96 when the bar took the reference's
     two-row shape: a 1080p garrison row of seven 62px pockets spent 700px of
     its 1180 on bare leather, and the classic HD's own sockets are ~112px on a
     1330px panel. 80 rows of art puts the 245-321-row sheets on a divisor of
     4 (61-80px), where 54 put them on 5 and 6. */
  /* 100x86 for the console's 112x96 bust sockets: the 245-321-row sheets land
     on divisor 3 or 4 (61-107 rows), which is what fills the socket the way
     the reference's painted busts fill theirs. */
  wide: Object.freeze({ w: 124, h: 84 }),
  /* 58x72 compact, up from 46x63 for the same reason: two rows of seven 46px
     pockets left 500px of a 1366px bar bare. 56 rows of art keeps the
     245-321-row sheets on a divisor of 5 or 6. */
  compact: Object.freeze({ w: 72, h: 62 }),
});

/**
 * Glyph pixels a name plate has to set inside, and how many lines it holds.
 *
 * A GUTTER, not the plate's interior. The old values were 60 and 44 against
 * plate interiors of exactly 60 and 44, so a name that happened to fill its
 * line touched the bezel on both sides: measured on the shipped Fenmire FOR
 * SALE row, Scaleshot Veteran and Ironhide Elder cleared the chamfer by 1px each
 * against Mire Tyrant's 15px. The "0 truncations across 181 names" that
 * signed the old numbers off is true and blind to this - it tested for glyph
 * loss, and the defect is a name that fits perfectly and looks jammed. Four
 * glyph pixels a side wraps those two to the two lines their neighbours
 * already use.
 *
 * Measured against all 181 creature names at these values: two 52px lines hold
 * every one of them and hyphenate three; three 38px lines hold every one of
 * them and hyphenate forty-three. `town-place.spec.ts` holds the gutter and
 * `town-bar.spec.ts` holds the no-glyph-lost rule.
 */
export const NAME_CAP = Object.freeze({
  wide: Object.freeze({ width: 52, lines: 2 }),
  compact: Object.freeze({ width: 38, lines: 3 }),
});

/**
 * Glyph pixels of a broken word that must stay on each side of the break.
 *
 * The width test on its own put the break wherever the line happened to run
 * out, and what shipped was "Dreadnoug-/ht" - two letters on a line of their
 * own, which no printed book and no shipped game does. Three either side is
 * the ordinary rule for English and it is the one the critic named; measured
 * against all 181 creature names it changes 3 of the 46 breaks the wide and
 * compact plates make between them and overflows neither.
 */
const MIN_STEM = 3;

/**
 * Where inside a word the break is allowed to land.
 *
 * The width test alone picks the LAST break that fits, which is the worst legal
 * one: it shipped "Dreadnou-/ght" and then, once `MIN_STEM` was in, "Dreadnou-"
 * still - a break through the `ou` digraph that the eye has to repair. English
 * hyphenation without a dictionary has one rule that is nearly always right and
 * never absurd, which is to divide a word between two consonants, so the widest
 * legal break is walked back to the nearest consonant pair and only falls back
 * to the width answer when the word has none. "Steam Colossus" then divides
 * "Dread-/nought", which is where a printer would put it.
 *
 * `y` counts as a vowel here. It is a consonant in `yeti` and a vowel in
 * `Cave Crawler`, and of the two mistakes only the second one splits a syllable.
 */
const isConsonant = (ch: string): boolean => {
  const c = ch.charCodeAt(0);
  const letter = (c >= 65 && c <= 90) || (c >= 97 && c <= 122);
  return letter && !"aeiouyAEIOUY".includes(ch);
};

/**
 * Consonant pairs that spell ONE sound, and so are not a place to divide.
 *
 * Without this the consonant rule is worse than the width test it replaces: it
 * put "Wreck-S-/hark", "Rot-Sc-/hool" and "Leprec-/haun" on the plate, because
 * `S|h` and `c|h` are two consonants and one phoneme. Anything ending in `h` is
 * a digraph in English; the other four are the clusters this roster actually
 * contains.
 */
const isDigraph = (a: string, b: string): boolean =>
  b === "h" || b === "H" || ["ck", "ng", "qu", "wr"].includes((a + b).toLowerCase());

/**
 * How far back from the widest legal break the consonant rule may reach.
 *
 * Unbounded it stops being a hyphenation rule and becomes a different one: a
 * word with a consonant pair near its front breaks there and leaves most of
 * itself on the second line, which is a worse-looking plate than the tight
 * break it replaced. Three is what "Steam Colossus" needs (the widest break that
 * fits is after `Dreadnou`, and `Dread-/nought` is three back) and it is the
 * whole of the reach.
 */
const STEM_REACH = 3;

/**
 * A creature name, broken to fit a plate the width of one socket.
 *
 * Words first; a word that will not fit a line on its own is HYPHENATED rather
 * than cut, because "Bellweth-/er" is a typographic device a reader already
 * knows and "Bellweth/er" is a bug. The `slice` at the end is a backstop only:
 * measured against all 181 creature names, neither plate overflows its line
 * budget, and the socket's `title` and `aria-label` carry the whole name.
 *
 * Two rules decide WHERE the break goes, and both exist because the break the
 * width test picks on its own is the worst legal one. `MIN_STEM` keeps a real
 * chunk on each side. And a break is never placed against a hyphen the name
 * already carries - "Wreck--/Shark" and "Dune--/Devourer" would read as a
 * typo rather than as a break.
 *
 * Breaking a compound AT its own hyphen is the reading a typesetter would
 * reach for first and it is not available here: `town-bar.spec.ts` rebuilds
 * the name by stripping a trailing hyphen off every line but the last, so
 * ["Wreck-", "Shark"] rebuilds as "WreckShark" and the name is reported lost.
 * The soft break is placed as far from the hard hyphen as the width allows
 * instead.
 */
export function wrapPlateName(
  text: string, capWidth: number, maxLines: number,
): readonly string[] {
  const width = (s: string): number => measureBitmapText(s).width;
  const fits = (head: string): boolean =>
    width(`${head}-`) <= capWidth && !head.endsWith("-");
  const tokens: string[] = [];
  for (const word of text.split(" ")) {
    let rest = word;
    while (width(rest) > capWidth && rest.length >= MIN_STEM * 2) {
      let k = rest.length - MIN_STEM;
      while (k > MIN_STEM && !fits(rest.slice(0, k))) k -= 1;
      if (!fits(rest.slice(0, k))) break;
      // Walking BACK from a break that fits can only shorten the head, so the
      // width test the loop above passed still holds, and a hyphen the name
      // carries is not a consonant so the rule cannot land beside one.
      const floor = Math.max(MIN_STEM, k - STEM_REACH);
      for (let j = k; j >= floor; j--) {
        if (!isConsonant(rest.charAt(j - 1)) || !isConsonant(rest.charAt(j))) continue;
        if (isDigraph(rest.charAt(j - 1), rest.charAt(j))) continue;
        k = j;
        break;
      }
      tokens.push(`${rest.slice(0, k)}-`);
      rest = rest.slice(k);
    }
    if (rest.length > 0) tokens.push(rest);
  }
  const lines: string[] = [];
  let line = "";
  for (const t of tokens) {
    const joined = line === "" ? t : `${line} ${t}`;
    if (line === "" || width(joined) <= capWidth) line = joined;
    else { lines.push(line); line = t; }
  }
  if (line !== "") lines.push(line);
  return lines.slice(0, maxLines);
}

/**
 * The frame a UI socket draws a creature with.
 *
 * One sheet covers a tier AND its upgrade - the file is named for the pair,
 * `06_lancer_sun_lancer.png` - so both forms resolve to the same key, and the
 * two adjacent sockets of a tier drew the identical sprite twice. Measured on
 * the shipped Castle bar: Angel and High Radiant, Archer and Master Bowyer, Skyclaw
 * and Crowned Skyclaw, Lancer and Champion were four pairs of pixel-identical
 * neighbours.
 *
 * The sheet's six columns are six ACTIONS (the atlas manifest names them: idle,
 * weapon hefted, forward swing, low chop, rush, crouch) and its two ROWS are
 * front and rear views, not two tiers. So the honest distinction the art can
 * carry is the stance: the base takes the idle one and each step up the chain
 * takes the next. Every one of the 85 sheets has a healthy column 1 - checked
 * against the shipped atlas, none is under 45% of column 0 on either axis.
 *
 * Depth in the chain, NOT the `upgraded` flag: the expansion's Saltreach tier 3 is a
 * three-step chain (Freebooter -> Sea Reaver -> Old Salt) whose top two forms are both
 * `upgraded`, so a boolean drew two of the three identically and left the
 * defect standing on exactly the town this rule exists for.
 *
 * ## The chain starts at column ONE, and column 0 is unreachable from here
 *
 * Column 0 is `idle stance at attention` and the artist drew it FACING THE
 * VIEWER on every sheet; columns 1 upward are the three-quarter action poses.
 * So a base form took the frontal drawing and its own upgrade the side one, and
 * the pair read as two unrelated creatures - the Lancer a 22px head-on
 * jumble, the Champion beside it a 40px three-quarter charge you can parse at a
 * glance. Measured across all 92 shipped sheets: column 0 is a median 0.89 of
 * the width of its neighbours and under 0.80 on 23 of them, and the base/upgrade
 * width ratio comes from 11 of 92 pairs under 0.70 to 5 of 92 by taking columns
 * 1 and 2 instead of 0 and 1. One facing per row, and the two forms of a tier
 * are still two different drawings.
 */
const SOCKET_COL0 = 1;

export function socketCreatureKey(
  content: ContentRegistry, def: CreatureDef,
): string | null {
  let frame = SOCKET_COL0;
  let cur: CreatureDefId | null = def.upgradeOf;
  // Bounded: the chain is acyclic by content validation, and the sheet has six
  // columns, so a chain deeper than that would wrap rather than fail.
  while (cur !== null && frame < 5) {
    const prev: CreatureDef | undefined = content.creatures.get(cur);
    if (prev === undefined) break;
    frame += 1;
    cur = prev.upgradeOf;
  }
  return creatureIcon(def, frame);
}

/**
 * The box to hand `MaybeIcon` so that every frame of one unit sheet draws at
 * one scale.
 *
 * `fitSpriteBox` fits the TRIMMED frame, and the six action columns of a sheet
 * are trimmed to six different rectangles - so a Lancer's idle column (130 x
 * 281) took divisor 6 and drew 21px wide while the Champion beside it, which
 * is the SAME creature's sheet one column along (225 x 242), took divisor 5 and
 * drew 45px. Two forms of one tier, adjacent sockets, one of them half again
 * the size of the other, and nothing about the art saying why.
 *
 * `srcH` is the sheet's own cell height and it is the same for all twelve
 * frames of a sheet - the packer records it precisely because the trim is per
 * frame. Choosing the divisor against the CELL rather than against the frame
 * puts a creature and its own upgrade on one scale and lets each pose occupy
 * the share of the cell the artist drew it at: the Champion's charge is lower
 * and wider than the Lancer's stand, which is what a charge is.
 *
 * The box handed back is `ceil(frame / divisor)` rather than the exact drawn
 * size. `fitSpriteBox` recomputes the divisor from the box it is given, and a
 * floored box is a hair SMALLER than the frame at that divisor, which rounds it
 * one divisor further down; a ceiled box is a hair larger, which cannot, as
 * long as the frame is bigger than `d^2 - d` on its short side. Every unit
 * frame in the atlas is over a hundred pixels on both.
 */
export function sheetFitBox(
  frame: { readonly w: number; readonly h: number; readonly srcH: number },
  box: { readonly w: number; readonly h: number },
): { readonly w: number; readonly h: number } {
  const cell = frame.srcH > 0 ? frame.srcH : frame.h;
  // The width guard is against the frame and not the cell: `srcW` is trimmed
  // per frame, so there is no cell width to divide, and a pose wider than the
  // socket has to come down whatever its cell says.
  const div = Math.max(1, Math.ceil(cell / box.h), Math.ceil(frame.w / box.w));
  return { w: Math.ceil(frame.w / div), h: Math.ceil(frame.h / div) };
}

/**
 * A PORTRAIT, the reference's way.
 *
 * The classic fills every army socket with a painted head-and-shoulders
 * (CPRSMALL, 58x64), never with a cropped battle sprite. The round-1 bust
 * rule - fit by width alone at divisor 2, seat at the top, crop at the
 * waist - put a horse's back and a spear shaft in the Gravewatch sockets and
 * no face at all, because a mounted or tall frame at divisor 2 is 130+ rows
 * of art in an 84-row pocket and the window landed on the middle of it.
 *
 * The rule now is a WINDOW: the upper `PORTRAIT_WINDOW` of the figure's
 * height, fitted to the pocket by whichever of width or window-height binds,
 * at a whole divisor. A 179x263 dread knight lands on divisor 2 with the
 * rider's head in the top third; a 231x150 dragon lands on divisor 2 and
 * shows whole; a 60x190 wraith is divisor 2 with its cowl centred. Checked
 * on all 170 front frames in the shipped atlas on a contact sheet
 * (scratch proto/contact_*.png) before this went in, not derived.
 */
export const PORTRAIT_WINDOW = 0.6;
/** Rows of the figure's own height cropped off the top: a raised weapon tip. */
export const PORTRAIT_SKIM = 0.04;
/** The keyline and bezel each side of the pocket that the art must clear. */
const PORTRAIT_INSET = 3;

export function portraitFitBox(
  frame: { readonly w: number; readonly h: number },
  boxW: number,
  boxH: number,
): { readonly w: number; readonly h: number; readonly top: number } {
  const innerW = Math.max(1, boxW - PORTRAIT_INSET * 2);
  const innerH = Math.max(1, boxH - PORTRAIT_INSET * 2);
  const div = Math.max(1, Math.ceil(Math.max(frame.w / innerW, (frame.h * PORTRAIT_WINDOW) / innerH)));
  return {
    w: Math.ceil(frame.w / div),
    h: Math.ceil(frame.h / div),
    top: PORTRAIT_INSET - Math.round((frame.h * PORTRAIT_SKIM) / div),
  };
}

/** Kept for the callers that sized a bust by width alone; the sockets no
 *  longer use it. */
export function bustFitBox(
  frame: { readonly w: number; readonly h: number },
  boxW: number,
  minDiv: number,
): { readonly w: number; readonly h: number } {
  const div = Math.max(minDiv, Math.ceil(frame.w / boxW));
  return { w: Math.ceil(frame.w / div), h: Math.ceil(frame.h / div) };
}

export const BUST_SLIDE = 0.34;

/** The pocket the portrait window is fitted to: the socket itself, both
 *  viewports. `--hs-socket-w/h` in the sheet below MUST agree. */
export const SOCKET_BOX = Object.freeze({
  wide: Object.freeze({ w: 132, h: 84 }),
  compact: Object.freeze({ w: 78, h: 62 }),
  /** Landscape phone. Must match `--hs-socket-w/h` under `.hm-root--phone`. */
  phone: Object.freeze({ w: 44, h: 40 }),
});

export function TownCreatureSlot(props: {
  readonly content: ContentRegistry;
  readonly slot: ArmySlot | null;
  readonly dragging?: boolean;
  readonly dropState?: "none" | "ok" | "illegal";
  readonly title?: string | undefined;
  readonly armyId?: string | undefined;
  readonly slotIndex?: number | undefined;
  readonly onClick?: (() => void) | undefined;
  readonly onDragStart?: (e: { shiftKey: boolean }) => void;
  /**
   * The modifiers are read off the DROP, not off the drag start.
   *
   * Chromium will not begin an HTML5 drag at all if a modifier is already down
   * when the button goes down - `dragstart` never fires - so a player who holds
   * Shift and then presses the mouse gets nothing whatsoever. Held down at any
   * point before the button comes up, the modifier is on the `drop` event and
   * the drag behaves. Measured in this app, not assumed.
   */
  readonly onDrop?: (e: DropModifiers) => void;
  readonly onDragEnter?: () => void;
  /** Finger-drag: the socket under the captured pointer, including other rows. */
  readonly onAim?: ((army: string, slot: number) => void) | undefined;
  readonly onDropAt?: ((army: string, slot: number, mods: DropModifiers) => void) | undefined;
  readonly onPointerCancel?: (() => void) | undefined;
  /** The pointer is over this socket (true) or has left it (false); the bar
   *  prints the stack's name on its status line, the reference's way. */
  readonly onHover?: ((over: boolean) => void) | undefined;
}): JSX.Element {
  const atlas = useAtlas();
  const compact = useCompactTown();
  const phone = usePhoneLayout();
  const countScale = useBitmapScale(2);
  const def = props.slot === null ? null : props.content.creatures.get(props.slot.def) ?? null;
  const key = def === null ? null : socketCreatureKey(props.content, def);
  const frame = key === null ? null : atlas.frame(key);
  const box = phone.phone ? SOCKET_BOX.phone : compact ? SOCKET_BOX.compact : SOCKET_BOX.wide;
  const drawn = frame === null ? null : portraitFitBox(frame, box.w, box.h);
  const drop = props.dropState ?? "none";
  const label = def === null
    ? "Empty"
    : `${groupDigits(props.slot?.count ?? 0)} ${def.namePlural}`;
  const socketKey = props.armyId !== undefined && props.slotIndex !== undefined
    ? townSocketKey(props.armyId, props.slotIndex)
    : undefined;
  const skipClick = useRef(false);
  const press = useRef<{
    pointerId: number;
    x: number;
    y: number;
    started: boolean;
    aimedArmy: string | null;
    aimedSlot: number | null;
  } | null>(null);

  const onPointerDown = (e: ReactPointerEvent<HTMLButtonElement>): void => {
    if (e.pointerType === "mouse") return;
    if (props.slot === null || props.onDragStart === undefined) return;
    skipClick.current = false;
    press.current = {
      pointerId: e.pointerId, x: e.clientX, y: e.clientY,
      started: false, aimedArmy: null, aimedSlot: null,
    };
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLButtonElement>): void => {
    const s = press.current;
    if (s === null || e.pointerId !== s.pointerId) return;
    if (!s.started) {
      if (!pastSocketSlop({ x: s.x, y: s.y }, { x: e.clientX, y: e.clientY })) return;
      s.started = true;
      skipClick.current = true;
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* released */ }
      props.onDragStart?.({ shiftKey: e.shiftKey });
    }
    const hit = socketFromPoint(e.clientX, e.clientY);
    if (hit === null) return;
    if (s.aimedArmy === hit.army && s.aimedSlot === hit.slot) return;
    s.aimedArmy = hit.army;
    s.aimedSlot = hit.slot;
    props.onAim?.(hit.army, hit.slot);
  };
  const finishPointer = (e: ReactPointerEvent<HTMLButtonElement>, cancelled: boolean): void => {
    const s = press.current;
    if (s === null || e.pointerId !== s.pointerId) return;
    press.current = null;
    if (!s.started) return;
    skipClick.current = true;
    markTownPointerDrop();
    if (cancelled) { props.onPointerCancel?.(); return; }
    const hit = socketFromPoint(e.clientX, e.clientY);
    if (hit === null) { props.onPointerCancel?.(); return; }
    props.onDropAt?.(hit.army, hit.slot, {
      shiftKey: e.shiftKey, altKey: e.altKey, ctrlKey: e.ctrlKey,
    });
  };

  return (
    <span className="hs-tcell">
      <button
        type="button"
        className={[
          "hs-tslot",
          props.slot === null ? "hs-tslot--empty" : "",
          props.dragging === true ? "hs-tslot--dragging" : "",
          drop === "ok" ? "hs-tslot--drop" : "",
          drop === "illegal" ? "hs-tslot--illegal" : "",
        ].filter((c) => c.length > 0).join(" ")}
        title={props.title ?? label}
        aria-label={label}
        data-town-socket={socketKey}
        draggable={
          props.slot !== null && props.onDragStart !== undefined && !phone.phone
        }
        onDragStart={(e) => props.onDragStart?.({ shiftKey: e.shiftKey })}
        onDragOver={(e) => { if (props.onDrop !== undefined) e.preventDefault(); }}
        onDragEnter={() => props.onDragEnter?.()}
        onDrop={(e) => {
          e.preventDefault();
          props.onDrop?.({ shiftKey: e.shiftKey, altKey: e.altKey, ctrlKey: e.ctrlKey });
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={(e) => finishPointer(e, false)}
        onPointerCancel={(e) => finishPointer(e, true)}
        onClick={(e) => {
          if (skipClick.current || townClickSwallowed()) {
            e.preventDefault();
            e.stopPropagation();
            skipClick.current = false;
            return;
          }
          props.onClick?.();
        }}
        onMouseEnter={() => props.onHover?.(true)}
        onMouseLeave={() => props.onHover?.(false)}
      >
        {key === null || frame === null || drawn === null ? null : (
          <MaybeIcon
            atlasKey={key}
            size={drawn.w}
            boxHeight={drawn.h}
            className="hs-tslot__art"
            style={{ top: `${String(drawn.top)}px` }}
            title={def?.name ?? ""}
          />
        )}
        {/* Drawn on an empty socket too. It used to be rendered only where
            there was a count, and the plinth is 13 of the socket's 68 rows -
            so on the four factions that start with an under-filled garrison
            the empty sockets' recess floor ran 13 rows lower than their
            neighbours' and the row had two different bottom lines. */}
        {/* Stamped INSIDE the socket, lower right, over the bust: the
            reference's placement. Keylined, because what is behind the figure
            is the creature's own feet and the build does not control it. No
            name plate under the socket any more - the name goes to the status
            line on hover and to the tooltip, which is where the classic keeps it, and
            a plate the width of one socket hyphenated Glaivewarden at 1366. */}
        <span className={props.slot === null
          ? "hs-tslot__count hs-tslot__count--empty" : "hs-tslot__count"}>
          {props.slot === null ? null
            : <BitmapText scale={countScale} outline>{compactCount(props.slot.count)}</BitmapText>}
        </span>
      </button>
    </span>
  );
}

// -------------------------------------------------------------- army blocks

/** Which modifiers were held when the stack was dropped. */
export interface DropModifiers {
  readonly shiftKey: boolean;
  readonly altKey: boolean;
  readonly ctrlKey: boolean;
}

export interface DragState {
  readonly army: string;
  readonly slot: number;
  readonly single: boolean;
}

export function TownArmyBlock(props: {
  readonly content: ContentRegistry;
  readonly army: ArmyState | null;
  readonly head?: ReactNode;
  /** Drawn full height to the left of the row: the visiting hero's face. */
  readonly side?: ReactNode;
  /**
   * Which of the three rows this is.
   *
   * Garrison, visiting army and dwellings were the same dark socket with the
   * same number in the same corner, so nothing on the bar said which troops
   * you own and which you can buy. The socket's metal now differs per row.
   */
  readonly tone?: "garrison" | "hero" | "market";
  readonly drag: DragState | null;
  /** The socket under the pointer, carrying the kernel's verdict on the drop. */
  readonly target: { readonly army: string; readonly slot: number; readonly reason: Reason | null } | null;
  /** The kernel's sentence for a refused drop, for the socket's own tooltip. */
  readonly targetTitle?: string | undefined;
  readonly onDragStart: (armyId: string, slot: number, single: boolean) => void;
  readonly onDragEnter: (armyId: string, slot: number) => void;
  readonly onDrop: (armyId: string, slot: number, mods: DropModifiers) => void;
  readonly onPointerCancel?: (() => void) | undefined;
  /** Clicking a stack, which is where splitting and dismissing live. */
  readonly onPick?: ((armyId: string, slot: number) => void) | undefined;
  /** The socket under the pointer, or null when it leaves the row. */
  readonly onHover?: ((armyId: string, slot: number | null) => void) | undefined;
}): JSX.Element {
  const slots = props.army?.slots ?? [null, null, null, null, null, null, null];
  const id = (props.army?.id ?? "") as string;
  const aimed = (i: number): boolean =>
    props.target !== null && props.target.army === id && props.target.slot === i;
  return (
    <div className={`hs-tarmy hs-tarmy--${props.tone ?? "garrison"}`}>
      {props.side ?? null}
      <div className="hs-tarmy__col">
      {props.head === undefined ? null : <div className="hs-tarmy__head">{props.head}</div>}
      <div className="hs-tarmy__slots">
        {slots.map((slot, i) => (
          <TownCreatureSlot
            key={`${id}:${String(i)}`}
            content={props.content}
            slot={slot}
            armyId={id}
            slotIndex={i}
            dragging={props.drag !== null && props.drag.army === id && props.drag.slot === i}
            dropState={!aimed(i) ? "none" : props.target?.reason === null ? "ok" : "illegal"}
            title={aimed(i) && props.target?.reason !== null ? props.targetTitle : undefined}
            onDragStart={(e) => props.onDragStart(id, i, e.shiftKey)}
            onDragEnter={() => props.onDragEnter(id, i)}
            onDrop={(e) => props.onDrop(id, i, e)}
            onAim={props.onDragEnter}
            onDropAt={props.onDrop}
            onPointerCancel={props.onPointerCancel}
            onClick={props.onPick === undefined ? undefined : () => props.onPick?.(id, i)}
            onHover={props.onHover === undefined ? undefined
              : (over) => props.onHover?.(id, over ? i : null)}
          />
        ))}
      </div>
      </div>
    </div>
  );
}

/**
 * The visiting hero, at the size the most important object on the bar deserves.
 *
 * The old tab was a 22px portrait in a 26px strip floating over the row, which
 * read as a stuck tooltip rather than as part of the layout. This is a plate
 * the full height of the bar, standing to the left of the hero's own troops,
 * so the face and the seven slots read as one block.
 *
 * And it is PACKED, which it was not. At 165x124 it carried a 60x76 portrait
 * and two short lines: a 20px band of unmarked gold above, 28px below and a
 * 105x55 void right of the face - about 45% of the biggest, brightest field on
 * a bar where every other group is filled to its plate. The classic's town
 * panel puts the four primary skills in that same footprint, and so does this:
 * six readings the player would otherwise have to open the hero sheet for.
 * Primaries come from the kernel's resolved `primary` when the host hands it
 * over; the stored `HeroState.primary` is only the fallback.
 *
 * The pip grid contributes NOTHING to the plate's intrinsic width (`width: 0`
 * with a percentage minimum, see `styles.ts`), so the plate is still exactly as
 * wide as the hero's name and class line make it and the seven sockets beside
 * it did not move.
 */
export function HeroPlate(props: {
  readonly hero: HeroState | null;
  readonly def: HeroDef | null;
  /** Resolved primaries from the kernel; falls back to the stored base. */
  readonly primary?: PrimarySkills;
  readonly onOpen?: (() => void) | undefined;
}): JSX.Element {
  if (props.hero === null || props.def === null) {
    return (
      <span className="hs-heroplate hs-heroplate--none">
        <span className="hs-heroplate__frame" />
        <span className="hs-heroplate__text">
          <b><BitmapText face="serifDisplay" shadow scale={1}>No hero</BitmapText></b>
          <i><BitmapText face="serif" shadow scale={1}>Hire one at the tavern</BitmapText></i>
        </span>
      </span>
    );
  }
  const def = props.def;
  const hero = props.hero;
  const primary = props.primary ?? hero.primary;
  const pips: readonly { readonly glyph: string; readonly value: string; readonly title: string }[] = [
    { glyph: "sword", value: String(primary.attack), title: "Attack" },
    { glyph: "shield", value: String(primary.defense), title: "Defence" },
    { glyph: "orb", value: String(primary.spellPower), title: "Spell power" },
    { glyph: "book", value: String(primary.knowledge), title: "Knowledge" },
    // Plain digits, not grouped: a comma in a 40px cell buys nothing a player
    // reading four figures of movement needs.
    { glyph: "boot", value: String(Math.trunc(hero.movementLeft)), title: "Movement left" },
    { glyph: "flask", value: String(Math.trunc(hero.mana)), title: "Spell points" },
  ];
  return (
    <button type="button" className="hs-heroplate" onClick={() => props.onOpen?.()}
      title={`${def.name} - open the hero sheet`}>
      {/* FULL BLEED, and the niche is what crops it. A 58px square portrait
          centred in a 60x92 niche left 34 rows of bare quantised stone - 17
          above the head and 17 below the shoulders, 37% of the frame - which
          is the same emptiness the pip grid was added to close, wearing the
          floor instead of the brass. The source is 256 square and the subject
          is a centred bust in every one of the 199 shipped portraits, so
          drawing it at the niche's HEIGHT and letting the niche take 17% off
          each side keeps the head and the collar and fills the frame edge to
          edge, which is what a mounted portrait looks like. */}
      <span className="hs-heroplate__frame">
        <MaybeIcon atlasKey={heroPortrait(def.portrait)} size={92} title={def.name} />
      </span>
      {/* Stamped, and it is the last serif that was left on the bottom bar.
          The display face at magnification 1 is cap 11, which is the cap
          height a 15px Palatino renders at, so the name holds the size it had
          and gives up the anti-aliasing; the class line takes the cap-9 text
          face, the same one the FOR SALE and GARRISON labels are set in. */}
      <span className="hs-heroplate__text">
        <b><BitmapText face="serifDisplay" shadow scale={1}>{def.name}</BitmapText></b>
        <i>
          <BitmapText face="serif" shadow scale={1}>
            {`${def.heroClass} · level ${String(hero.level)}`}
          </BitmapText>
        </i>
        <span className="hs-heropips">
          {pips.map((pip) => (
            <span key={pip.title} className="hs-heropip" title={`${pip.title}: ${pip.value}`}>
              <Glyph name={pip.glyph} size={8} />
              <BitmapText face="serif" shadow scale={1}>{pip.value}</BitmapText>
            </span>
          ))}
        </span>
      </span>
    </button>
  );
}

// ------------------------------------------------------------ dwelling strip

export interface DwellingLine {
  readonly def: CreatureDef;
  readonly available: number;
  /** What one costs, so a market socket can never be read as a stack size. */
  readonly gold: number;
}

/**
 * One socket per tier, not one per creature form.
 *
 * `recruitableCreatures` lists the base and the upgrade separately, which is
 * right for the recruit dialog and wrong for a bar: fourteen sockets do not fit
 * beside two armies, and the two forms of a tier draw from one pool anyway. The
 * upgraded form wins because that is what the dwelling now produces.
 */
export function dwellingLines(
  content: ContentRegistry, town: TownState,
): readonly DwellingLine[] {
  const byTier = new Map<number, CreatureDef>();
  for (const def of recruitableCreatures(content, town)) byTier.set(def.tier, def);
  return [...byTier.keys()].sort((a, b) => a - b).map((tier) => {
    const def = byTier.get(tier)!;
    return {
      def,
      available: availableOf(content, town, def.id),
      gold: def.cost.gold ?? 0,
    };
  });
}

// ------------------------------------------------------------- town readouts

export interface TownStats {
  readonly defence: string;
  readonly guild: string;
  readonly growth: number;
  readonly built: number;
  readonly buildable: number;
}

/** The four numbers a player checks before deciding what to build. */
export function townStats(content: ContentRegistry, town: TownState): TownStats {
  const built = new Set<string>(town.built.map((b) => b as string));
  let buildable = 0;
  let defence = "Village";
  // The strongest fort STEP, not the last one the id sort happens to reach.
  // Sorted by id a built Castle town yields sunspire_castle, sunspire_citadel,
  // sunspire_fort - so "keep the last match" printed FORT on a town with a
  // Castle in it, on all twelve. The chain is `upgradeOf` links, so depth in
  // that chain is the only ordering that means anything.
  let defenceDepth = -1;
  for (const id of [...content.buildings.keys()].sort()) {
    const def = content.buildings.get(id);
    if (def === undefined || (def.faction as string) !== (town.faction as string)) continue;
    buildable += 1;
    if (def.slot !== "fort" || !built.has(def.id as string)) continue;
    const depth = chainDepth(content, def.id);
    if (depth > defenceDepth) { defence = def.name; defenceDepth = depth; }
  }
  const guild = guildLevelOf(content, town);
  const growth = dwellingLines(content, town)
    .reduce((sum, l) => sum + l.available, 0);
  return {
    defence,
    guild: guild === 0 ? "None" : `Level ${String(guild)}`,
    growth,
    built: built.size,
    buildable,
  };
}

/** Kept next to its readers so the strip and the dialog cannot disagree. */
export const useTownStats = (
  content: ContentRegistry, town: TownState,
): TownStats => useMemo(() => townStats(content, town), [content, town]);

// ------------------------------------------------------------- the console

/**
 * The velvet the console is upholstered in, per owner.
 *
 * Three values a colour: the lit crown of the plate, its body, and the shadow
 * at the foot. They are the eight the classic player colours as a dark cloth rather
 * than as the flat swatch `PLAYER_COLOURS` keeps for flags - a flag is a
 * saturated fill, a panel is a lit surface. A town nobody owns keeps the
 * leather every other screen is made of.
 */
export const VELVET: Readonly<Record<string, readonly [string, string, string]>> = Object.freeze({
  red: ["#a3302a", "#741e19", "#41100d"],
  blue: ["#3a5aa8", "#26407f", "#14234c"],
  tan: ["#a3844c", "#755c33", "#42331a"],
  green: ["#3f7f3c", "#2b5c2b", "#163117"],
  orange: ["#b86a2b", "#8a4b1c", "#4c2a10"],
  purple: ["#6f3f9c", "#4f2c75", "#2b1743"],
  teal: ["#2c857e", "#1d605c", "#0f3532"],
  pink: ["#ad5583", "#803d60", "#472136"],
});

/** The custom properties `TOWN_CSS` reads the console's colour from. */
export function velvetVars(colour: string | null | undefined): Readonly<Record<string, string>> {
  const v = colour === null || colour === undefined ? undefined : VELVET[colour];
  if (v === undefined) return {};
  return { "--hs-velvet-hi": v[0], "--hs-velvet": v[1], "--hs-velvet-lo": v[2] };
}

/**
 * The sky behind a bust, per faction.
 *
 * The classic's creature busts are painted on a backdrop that is the
 * faction's own weather - a blue sky behind a Castle spearwarden, sulphur behind
 * an Emberpit imp, a green murk behind a Gravewatch skeleton. The sprites here
 * are cut out by their alpha, so the console paints the backdrop: one dusk
 * gradient, tinted per faction. Three stops, high to low.
 */
export const BUST_SKY: Readonly<Record<string, readonly [string, string, string]>> = Object.freeze({
  sunspire: ["#7d94b8", "#46597a", "#1c1a16"],
  greenmantle: ["#8bb07a", "#4a6b48", "#171f12"],
  skyreach: ["#a4bbd8", "#5d6f93", "#1b1f2c"],
  emberpit: ["#b8582c", "#6b2416", "#1e0c08"],
  gravewatch: ["#6f8a78", "#33473c", "#0f1512"],
  deepvault: ["#6e4f92", "#3a2a55", "#120c1c"],
  ironhorde: ["#b8874e", "#6d4c2a", "#22160c"],
  fenmire: ["#6f8d5a", "#3d5535", "#141a10"],
  prismgate: ["#93a6cc", "#576a95", "#1b1b2a"],
  saltreach: ["#4f8fa8", "#2c5b6e", "#0e1e26"],
  cogworks: ["#bc9c6c", "#705a3a", "#201810"],
  frostmark: ["#a2b8ca", "#5f7280", "#1a2026"],
});

/** The custom properties the bust sockets paint their sky from. */
export function bustSkyVars(faction: string | null | undefined): Readonly<Record<string, string>> {
  const v = faction === null || faction === undefined ? undefined : BUST_SKY[faction];
  if (v === undefined) return {};
  return { "--hs-sky-hi": liftSky(v[0], 0.45, 255), "--hs-sky": liftSky(v[1], 0.3, 200), "--hs-sky-lo": v[2] };
}

/**
 * The portrait ground is LIGHTER than the dusk the busts stood on. Gravewatch
 * and Deepvault art is mostly bone, iron and black, and on a #33473c dusk it
 * measured as one dark mass with no face (critic round 1). CPRSMALL paints a
 * pale sky behind every head. Mix each stop toward a cream by `k`.
 */
function liftSky(hex: string, k: number, toward: number): string {
  const n = parseInt(hex.slice(1), 16);
  const ch = (c: number): string => Math.round(c * (1 - k) + toward * k).toString(16).padStart(2, "0");
  return `#${ch((n >> 16) & 255)}${ch((n >> 8) & 255)}${ch(n & 255)}`;
}

/**
 * A square carved button for the console's right column.
 *
 * The classic's are painted: the town hall, the fort, the marketplace, the
 * tavern - each button a small picture of the building it opens - and a
 * large gold checkmark to leave. So a button given `art` draws that atlas
 * frame (the town's OWN building, from the town painting's sheet) fitted
 * into the plate, and the 7x7 glyph is only the fallback for a control with
 * no building behind it. The leave plate draws a checkmark.
 */
export function TownIconButton(props: {
  readonly glyph: string;
  /** An atlas frame to paint on the plate instead of the glyph. */
  readonly art?: string | null | undefined;
  readonly title: string;
  readonly label?: string;
  readonly tone?: "bronze" | "gold";
  readonly wide?: boolean;
  readonly disabled?: boolean;
  readonly pressed?: boolean;
  readonly onClick: () => void;
}): JSX.Element {
  const dpr = useDevicePixelRatio();
  const label = props.label === undefined ? null : props.label.toUpperCase();
  // The 1366 console's plates are two thirds the size and the display face
  // (14 rows, 74px for LEAVE) does not fit their 69px; the text face (11
  // rows, 55px) does. Both are held to the window's cap floor either way.
  const labelFace = isCompactConsole() ? "serif" : "serifDisplay";
  const fit = label === null || props.wide !== true ? "both" : wideLabelFit(label, labelFace, dpr);
  return (
    <button
      type="button"
      className={`hs-ibtn${props.tone === "gold" ? " hs-ibtn--gold" : ""}${props.wide === true ? " hs-ibtn--wide" : ""}`}
      title={props.title}
      aria-label={props.title}
      disabled={props.disabled === true}
      aria-pressed={props.pressed === undefined ? undefined : props.pressed}
      onClick={props.onClick}
    >
      {props.art !== undefined && props.art !== null ? (
        <span className="hs-ibtn__art" aria-hidden="true">
          <MaybeIcon atlasKey={props.art} size={compactIconBox(props.wide === true)} />
        </span>
      ) : props.glyph === "leave" ? (
        fit === "label" ? null : <Checkmark />
      ) : (
        <Glyph name={props.glyph} size={24} />
      )}
      {label === null || fit === "check" ? null : (
        <span className={`hs-ibtn__text${fit === "label" ? " hs-ibtn__text--alone" : ""}`}>
          <BitmapText face={labelFace} shadow scale={1}>{label}</BitmapText>
        </span>
      )}
    </button>
  );
}

/** CSS pixels of plate on each flank of a wide plate's contents - the
 *  padding `.hs-ibtn--wide` declares. */
const WIDE_PLATE_INSET = 4;

/**
 * What the wide plate shows: its label and the check when both stand inside
 * the plate with `WIDE_PLATE_INSET` on each flank, the label alone when only
 * it does, and the check alone otherwise - the reference's own exit button
 * is icon-only. Measured as drawn: the label is held to the window's cap
 * floor (15 rows on a 1080p window), so under an 80% page it is 100 CSS px
 * in a plate that is 110, and at 100% on the same window it is 80 - the
 * check's 30 fits neither, and before this the last E sat under the bevel.
 */
function wideLabelFit(label: string, face: "serif" | "serifDisplay", dpr: number): "both" | "label" | "check" {
  const compact = isCompactConsole();
  // Two plate columns and their gap (`.hs-tfoot__side`), plus the 3px the
  // wide plate takes from the column's padding on each side.
  const plate = (compact ? 34 * 2 + 3 : 50 * 2 + 4) + 6 - 2 * WIDE_PLATE_INSET;
  // Plus the one-texel shadow `BitmapText` draws.
  const text = measureDrawnBitmapText(label, 1, face, dpr).width
    + resolveBitmapSize(face, 1, dpr).cssPerTexel;
  const check = compact ? 20 : 26 + 4;
  if (text + check <= plate) return "both";
  if (text <= plate) return "label";
  return "check";
}

/** Whether the console is on its 1366 layout (`COMPACT_QUERY`). */
const isCompactConsole = (): boolean =>
  typeof window !== "undefined" && window.matchMedia(COMPACT_QUERY).matches;

/** The picture's box on a plate: the plate less its chamfer, and a wide
 *  plate no taller than a square one. Read by the compact query so the
 *  1366 console's 34px plates get a 26px picture. */
function compactIconBox(wide: boolean): number {
  const compact = typeof window !== "undefined" && window.matchMedia(COMPACT_QUERY).matches;
  return (compact ? 26 : 42) - (wide ? 4 : 0);
}

/** The reference's exit: one large gold checkmark on the plate, smooth. */
function Checkmark(): JSX.Element {
  return (
    <svg className="hs-ibtn__check" viewBox="0 0 32 32" width="30" height="30" aria-hidden="true">
      <path d="M4 17 L12 25 L28 7" fill="none" stroke="rgba(0,0,0,0.55)" strokeWidth="7"
        strokeLinecap="round" strokeLinejoin="round" transform="translate(1,1.5)" />
      <path d="M4 17 L12 25 L28 7" fill="none" stroke="#3c2a06" strokeWidth="6"
        strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 17 L12 25 L28 7" fill="none" stroke="#fff3c8" strokeWidth="2.2"
        strokeLinecap="round" strokeLinejoin="round" transform="translate(0,-1)" opacity="0.7" />
    </svg>
  );
}

/** The strongest built step in a slot chain, for the hall and fort marks. */
export function topBuilt(
  content: ContentRegistry, town: TownState, slot: string,
): BuildingDef | null {
  let best: BuildingDef | null = null;
  let depth = -1;
  for (const id of town.built) {
    const def = content.buildings.get(id);
    if (def === undefined || def.slot !== slot) continue;
    const d = chainDepth(content, def.id);
    if (d > depth) { best = def; depth = d; }
  }
  return best;
}

/**
 * The console's left block: crest, the town's name, the hall and fort marks
 * with the day's income, and the seven growth tiles under them.
 */
export function TownConsoleHead(props: {
  readonly content: ContentRegistry;
  readonly town: TownState;
  readonly crest: string | null;
  readonly income: number;
  readonly lines: readonly DwellingLine[];
  readonly onRecruit: (c: CreatureDefId) => void;
  readonly onHover: (text: string | null) => void;
}): JSX.Element {
  const compact = useCompactTown();
  const hall = topBuilt(props.content, props.town, "hall");
  const fort = topBuilt(props.content, props.town, "fort");
  const mark = compact ? 30 : 44;
  /* The tile's window: the bust is fitted by width (see \`bustFitBox\`) and
     the pocket crops it; the height here is the crop line the slide is
     measured against. */
  const tile = compact ? { w: 56, h: 44 } : { w: 100, h: 54 };
  const atlas = useAtlas();
  return (
    <div className="hs-tfoot__left">
      <div className="hs-tfoot__head">
        <span className="hs-tfoot__crest" title={`${props.town.name}, a ${titleCase(props.town.faction as string)} town`}>
          <MaybeIcon atlasKey={props.crest} size={compact ? 46 : 68} boxHeight={compact ? 46 : 68} />
        </span>
        <div className="hs-tfoot__title">
          <span className="hs-tfoot__name" title={props.town.name}>
            <BitmapText face="serifDisplay" shadow scale={compact ? 1 : 2}>{props.town.name}</BitmapText>
          </span>
          <span className="hs-tfoot__marks">
            <span className="hs-tfoot__mark" title={hall === null ? "No hall" : hall.name}
              onMouseEnter={() => props.onHover(hall === null ? "No hall" : hall.name)}
              onMouseLeave={() => props.onHover(null)}>
              <MaybeIcon atlasKey={hall === null ? null : buildingIcon(hall.art)} size={mark} boxHeight={mark} />
            </span>
            <span className="hs-tfoot__mark" title={fort === null ? "No fort" : fort.name}
              onMouseEnter={() => props.onHover(fort === null ? "No fort" : fort.name)}
              onMouseLeave={() => props.onHover(null)}>
              <MaybeIcon atlasKey={fort === null ? null : buildingIcon(fort.art)} size={mark} boxHeight={mark} />
            </span>
            <span className="hs-tfoot__income" title={`${groupDigits(props.income)} gold a day`}
              onMouseEnter={() => props.onHover(`Income: ${groupDigits(props.income)} gold a day`)}
              onMouseLeave={() => props.onHover(null)}>
              <MaybeIcon atlasKey={RESOURCE_ICON.gold} size={compact ? 24 : 32} className="hm-relief__inlay" />
              <BitmapText face="serif" shadow scale={1}>{groupDigits(props.income)}</BitmapText>
            </span>
          </span>
        </div>
      </div>
      <div className="hs-tfoot__growth">
        {props.lines.map((l) => {
          const key = socketCreatureKey(props.content, l.def);
          const frame = key === null ? null : atlas.frame(key);
          const drawn = frame === null ? null : bustFitBox(frame, tile.w, compact ? 4 : 3);
          const slide = drawn === null ? 0 : Math.round(Math.max(0, drawn.h - tile.h) * BUST_SLIDE);
          return (
            <button type="button" className="hs-tgrow" key={l.def.id as string}
              title={`${l.def.namePlural}: ${groupDigits(l.available)} available this week at ${groupDigits(l.gold)} gold each`}
              onMouseEnter={() => props.onHover(`Recruit ${l.def.namePlural} (${groupDigits(l.available)} available, ${groupDigits(l.gold)} gold each)`)}
              onMouseLeave={() => props.onHover(null)}
              onClick={() => props.onRecruit(l.def.id)}>
              <span className="hs-tgrow__socket">
                {key === null || drawn === null ? null
                  : <MaybeIcon atlasKey={key} size={drawn.w} boxHeight={drawn.h} title={l.def.name}
                      style={{ top: `${String(3 - slide)}px` }} />}
              </span>
              <span className="hs-tgrow__n">
                <BitmapText face="serif" shadow scale={1}>{`+${groupDigits(l.available)}`}</BitmapText>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** The visiting hero's face at the head of his row; nothing else, the way the
 *  reference does it. The name and class go to the status line on hover. */
export function HeroFace(props: {
  readonly hero: HeroState | null;
  readonly def: HeroDef | null;
  readonly onOpen?: (() => void) | undefined;
  readonly onHover: (text: string | null) => void;
}): JSX.Element {
  const compact = useCompactTown();
  if (props.hero === null || props.def === null) {
    return (
      <span className="hs-tfoot__face hs-tfoot__face--none" title="No visiting hero"
        onMouseEnter={() => props.onHover("No visiting hero - hire one at the tavern")}
        onMouseLeave={() => props.onHover(null)} />
    );
  }
  const text = `${props.def.name}, level ${String(props.hero.level)} ${props.def.heroClass}`;
  return (
    <button type="button" className="hs-tfoot__face" title={`${text} - open the hero sheet`}
      onMouseEnter={() => props.onHover(text)}
      onMouseLeave={() => props.onHover(null)}
      onClick={() => props.onOpen?.()}>
      <MaybeIcon atlasKey={heroPortrait(props.def.portrait)} size={compact ? 64 : 96} title={props.def.name} />
    </button>
  );
}

/** The town's own pennant at the head of the garrison row. */
export function TownFlag(props: { readonly name: string; readonly onHover: (t: string | null) => void }): JSX.Element {
  return (
    <span className="hs-tfoot__face hs-tfoot__face--none" title={`${props.name} - the garrison`}
      onMouseEnter={() => props.onHover(`${props.name} garrison`)}
      onMouseLeave={() => props.onHover(null)}>
      <span className="hs-tfoot__flagpole" aria-hidden="true" />
      <span className="hs-tfoot__flag" aria-hidden="true" />
    </span>
  );
}

/** The resource strip along the foot of the console, with the date. */
export function TownResourceStrip(props: {
  readonly resources: Resources;
  readonly income: Resources | null;
  readonly day: number;
}): JSX.Element {
  const week = Math.floor((props.day - 1) / 7) % 4 + 1;
  const month = Math.floor((props.day - 1) / 28) + 1;
  const dayOfWeek = (props.day - 1) % 7 + 1;
  return (
    <div className="hs-tfoot__strip">
      <TownTreasury resources={props.resources} income={props.income} />
      <span className="hs-tfoot__date">
        <BitmapText face="serif" shadow scale={1}>
          {`Month: ${String(month)}, Week: ${String(week)}, Day: ${String(dayOfWeek)}`}
        </BitmapText>
      </span>
    </div>
  );
}
